import { DeskResolutionConfirmation } from '../lib/deskOrderResolution';
import { recordSoundDecision } from '../lib/audio/soundDiagnostics';
// ── AccountStore — Zustand store for Kalshi account state (MT relay) ──
//
// In the MT world, all Kalshi operations go through the relay API.
// Keys are stored server-side. The browser never signs requests directly.
// This store preserves the same state shape so views don't change.

import { create } from 'zustand';
import { assertSourceConfirmed } from '../lib/trading/sourceConfirmation';
import { apiFetch, getAuthSessionEpoch, useAuthStore } from './authStore';
import { useMarketStore } from './marketStore';
import { historicalIdentityEpoch, invalidateHistoricalIdentitySession, hydrateHistoricalIdentities, type HistoricalReadKind } from './accountMarketIdentity';
import { limitOrderRole } from '../views/ValueDashboard/tradeModalBook';
import type { VenueBook } from '../views/ValueDashboard/types';
import {
  canEditKalshiOrder,
  type KalshiPosition,
  type KalshiFill,
  type KalshiOrder,
  type OrderInput,
  type OscarSequenceInput,
  type OscarSequenceResult,
  type KeyHealth,
  type AccountStatus,
} from '../types/kalshi';
import { readSoundSettings } from '../lib/audio/soundSettings';
import { getChannelVolume } from '../lib/alerts/alertPreferences';
import type { AlertChannelId } from '../lib/alerts/registry';
import { playFillTone, playKaChingTone, playWhaleTone, playOrderRejectedTone, playOrderPlacedTone, tryFillSoundNow } from '../lib/audio/tradeAudio';
import { getAudioContext, setPendingSound, reportSynthAttemptHealth, reportFillCoordinationFailure } from '../lib/audio/audioContext';
import { coordinateFillAudio } from '../lib/audio/fillCoordination';
import { LimitRejectionError, isLimitRejectionPayload } from '../lib/limits';
import { mirrorCents, validateReplacePriceCents } from '../lib/pricing/subCent';
import { residualOpenContracts, MAX_CLOSE_ATTEMPTS } from '../lib/trading/closeResidual';
import {
  OrderSubmissionUnknownError,
  SelfTradeCancelledError,
  readSelfTradeCancel,
  type SelfTradeCancel,
} from '../lib/trading/orderSubmissionOutcome';
import { PullAllRefusedError } from '../lib/trading/bulkCancelAttemptAudit';
import {
  KALSHI_CANCEL_SYNC_TIMEOUT_MS,
  beginKalshiCancel,
  clearKalshiCancel,
  isKalshiCancelPending,
  markKalshiCancelAwaitingRemoval,
  markKalshiCancelTimedOut,
  reconcileKalshiCancels,
  addKalshiCancelTombstones,
  applyKalshiCancelTombstones,
  KALSHI_CANCEL_TOMBSTONE_MAX,
  kalshiTombstoneHides,
  markKalshiCancelRelisted,
  OrdersVintageGate,
  type KalshiCancelBatchResult,
  type KalshiCancelStates,
  type KalshiCancelTombstones,
} from '../lib/trading/kalshiCancelLifecycle';
import { reconstructOrderTraderSide } from '../lib/kalshiApi/orderReadV2';
import { probeAttachServerTiming, probeMark } from '../lib/trading/orderLatencyProbe';
import { parseServerTiming } from '../lib/trading/serverTiming';
import { emitPlacement, emitPlacementRemoved } from '../lib/trading/placementBus';
import { startVisibilityAwareInterval } from '../lib/visibilityThrottle';
import { invalidateRelayPushSession, onActiveChange, subscribePush, topicActive } from '../services/relayPush';
import { readKalshiReportedBalanceCents } from '../lib/kalshiBalance';
import { isOrgBalanceHiddenBody } from '../lib/orgBalanceHidden';
// One bound for the Recent Fills tape. The rail merges this Kalshi slice with
// the Poly relay read and caps the union at the same number, so the slice size
// here and the Poly request limit must track one constant (#457).
import { RECENT_FILL_LIMIT } from '../lib/fills/recentFillTape';
import { retainNewestFills } from '../lib/fills/fillBatchIngest';
import { createRelayClock, elapsedSince, localClockStamp, type RelayClock } from '../lib/relayClock';

const POLL_INTERVAL_MS = 5000;
export const KALSHI_PLACEMENT_CONFIRMATION_TIMEOUT_MS = 10_000;
// The relay's reconciliation backstop discovers fills every 10s when its
// Kalshi fills WebSocket is unavailable. A 10s client cutoff raced that same
// cadence: request, ingest, and delivery overhead could make a legitimate
// newly observed fill 10.001s old and silently suppress its sound (#528).
// Three backstop windows keep that boundary audible while remaining far too
// short to turn historical ledger hydration into an alert burst.
const FILL_SOUND_RECENCY_MS = 30_000;
/** How far ahead of the relay clock's upper bound a fill may appear and still
 * count as fresh. The bounds omit hello transit time and can drift over a long
 * stream; a garbage or far-future timestamp is still rejected. */
const FILL_SOUND_FUTURE_SKEW_MS = 2_000;
// A fresh page/session immediately hydrates recent fills through REST. Those
// rows predate the session and must stay silent even when they fall inside the
// reconciliation window above. Automatic reconnects intentionally preserve
// this baseline so fills created during a transient gap can still alert once
// discovered; an explicit disconnect followed by connect starts a new session.
let _fillSoundSessionStartedAtMs: number | null = null;
let _fillSoundSessionStartedMonoMs: number | null = null;
let _fillSoundSessionGeneration = 0;
let _watchFillAudioScope: (() => void) | null = null;
function watchFillAudioScope(): void {
  if (_watchFillAudioScope) return;
  const authIdentity = () => {
    const { status, user } = useAuthStore.getState();
    return JSON.stringify([status, user?.id, user?.orgId, user?.role]);
  };
  let identity = authIdentity();
  const stopAuth = useAuthStore.subscribe(() => {
    const next = authIdentity();
    if (next !== identity) { identity = next; _fillSoundSessionGeneration++; }
  });
  const stopMode = useAccountStore.subscribe((next, previous) => {
    if (next.keyMode !== previous.keyMode) _fillSoundSessionGeneration++;
  });
  _watchFillAudioScope = () => { stopAuth(); stopMode(); _watchFillAudioScope = null; };
}
if (import.meta.hot) import.meta.hot.dispose(() => _watchFillAudioScope?.());
const MAX_SEEN_FILLS = 1000;
const MAX_RECENT_FILLS = 100;

let _stopPollTimer: (() => void) | null = null;
let _stopFillPollTimer: (() => void) | null = null;
let _connectRetryTimer: ReturnType<typeof setTimeout> | null = null;
let _connectRetryAttempts = 0;
// #1205: polled balance reads can overlap; only the newest answer applies, so
// a slow read issued before a manager hid balances never repaints the figure.
let _balanceReadSeq = 0;
let _balanceAppliedSeq = 0;
let _connectionGeneration = 0;
let _closeAuthorityGeneration = 0;
// One in-flight REST positions read shared by every concurrent refresh caller.
let _positionsRefreshInFlight: { generation: number; started: boolean; promise: Promise<boolean> } | null = null;
const MAX_CONNECT_RETRIES = 5;
const BASE_CONNECT_RETRY_MS = 3_000;
// After fast retries exhaust, keep trying at a gentle cadence instead of
// dying permanently (status 'error' previously required a manual reload).
const SLOW_CONNECT_RETRY_MS = 60_000;
let _connectInFlight = false;

// Self-heal watchdog. connect()'s generation guards early-return on a stale
// generation (a disconnect()/remount that straddled an in-flight connect). That
// bare `return` skips BOTH the success path (which starts polling) AND the catch
// block (which schedules the retry ladder) — so a straddled connect leaves the
// store stranded at a non-'connected' status with NO retry timer and nothing to
// re-trigger it. That is the "API key: Connecting…" hang that only clears on a
// manual reload or by refreshing another window (the multi-window reconnect
// dance, Jun 27 diagnosis). The watchdog re-fires connect() ONLY when a
// connection is provably wanted and nothing is working toward it, so it can
// never compete with an active connect/retry or churn a healthy session.
let _desiredConnected = false;
let _healWatchdog: ReturnType<typeof setInterval> | null = null;
const HEAL_WATCHDOG_INTERVAL_MS = 10_000;

const _seenFillIds = new Set<string>();
const _seenFillOrder: string[] = [];
const _seenOrderIds = new Set<string>();

// ── #241 cancel fence bookkeeping ───────────────────────────────────────────
// Timers and attempt tokens live outside the store: they're machinery, not
// rendered state. Every cancel attempt gets a monotonic token, and the timer
// entry carries the token that armed it — so a DELETE that resolves late, or a
// timeout that fires after the row was reconciled away / retried / disconnected,
// can prove it is stale and do nothing instead of mutating a newer attempt.
let _cancelAttemptSeq = 0;
const _cancelAttempts = new Map<string, number>();
const _cancelTimers = new Map<string, { token: number; handle: ReturnType<typeof setTimeout> }>();

// A successful placement response is the first authoritative evidence that a
// resting order exists. Kalshi's immediately-following open-orders read can lag
// that write briefly, so keep the accepted order projected until one later
// authoritative snapshot confirms its id or this short fence expires. This is
// display reconciliation only: it never retries or changes an order payload.
interface PlacementConfirmation {
  order: KalshiOrder;
  expiresAtMs: number;
}
const _placementConfirmations = new Map<string, PlacementConfirmation>();

function forgetPlacementConfirmation(orderId: string): void {
  _placementConfirmations.delete(orderId);
  // The per-ticker lane's projection for this id must not outlive the order
  // it stood in for (cancel accepted, fill drained it, lifecycle confirmed).
  emitPlacementRemoved(orderId);
}

function forgetAllPlacementConfirmations(): void {
  _placementConfirmations.clear();
}

/** Whether an order shown in `openOrders` is still only the short-lived local
 * placement projection and has not yet appeared in an authoritative venue
 * snapshot. LT2's reconciliation timer uses this distinction so the projection
 * cannot accidentally count as proof of its own order. */
export function hasPendingKalshiPlacementConfirmation(
  orderId: string,
  nowMs = Date.now(),
): boolean {
  const confirmation = _placementConfirmations.get(orderId);
  if (!confirmation) return false;
  if (confirmation.expiresAtMs <= nowMs) {
    _placementConfirmations.delete(orderId);
    return false;
  }
  return true;
}

function reconcilePlacementConfirmations(
  authoritative: KalshiOrder[],
  nowMs = Date.now(),
): KalshiOrder[] {
  const ids = new Set(authoritative.map((order) => order.orderId));
  let next: KalshiOrder[] | null = null;
  for (const [orderId, confirmation] of _placementConfirmations) {
    if (ids.has(orderId)) {
      _placementConfirmations.delete(orderId);
      continue;
    }
    if (confirmation.expiresAtMs <= nowMs) {
      _placementConfirmations.delete(orderId);
      continue;
    }
    if (!next) next = authoritative.slice();
    next.push(confirmation.order);
  }
  return next ?? authoritative;
}

function clearCancelTimer(orderId: string): void {
  const timer = _cancelTimers.get(orderId);
  if (timer) {
    clearTimeout(timer.handle);
    _cancelTimers.delete(orderId);
  }
}

function forgetCancelAttempt(orderId: string): void {
  _cancelAttempts.delete(orderId);
  clearCancelTimer(orderId);
}

/** Arm the bounded escape from a stalled Open Orders synchronization for one
 *  claimed attempt. Shared by the single and batch paths so the two can never
 *  drift on which attempt a late timer is allowed to promote. */
function armCancelSyncTimer(orderId: string, token: number): void {
  const handle = setTimeout(() => {
    const timer = _cancelTimers.get(orderId);
    if (!timer || timer.token !== token) return;
    _cancelTimers.delete(orderId);
    if (_cancelAttempts.get(orderId) !== token) return;
    // Hands the control back with truthful copy. Never removes the row —
    // we don't know that the order is gone, only that we can't confirm it.
    useAccountStore.setState({
      kalshiCancelStates: markKalshiCancelTimedOut(
        useAccountStore.getState().kalshiCancelStates,
        orderId,
      ),
    });
  }, KALSHI_CANCEL_SYNC_TIMEOUT_MS);
  _cancelTimers.set(orderId, { token, handle });
}

function forgetAllCancelAttempts(): void {
  for (const timer of _cancelTimers.values()) clearTimeout(timer.handle);
  _cancelTimers.clear();
  _cancelAttempts.clear();
  _cancelTombstones = new Map();
  _editRetiring.clear();
  _ordersVintage.reset();
}

// ── #968 snapshot ordering + confirmed-cancel tombstones ───────────────────
// The push `orders` topic and HTTP reads of the same book can land out of
// order. Each carries the relay's `ordersAsOf`; an older one than the newest
// applied (same orders scope) is not a newer view of anything and is dropped
// whole, exactly as #916 does for the pos lane. Tombstones cover snapshots
// without that evidence. Both reset on disconnect with the cancel fence.
let _cancelTombstones: KalshiCancelTombstones = new Map();
const _ordersVintage = new OrdersVintageGate();

// #971: ids an edit's cancel-first leg retired. A 2xx DELETE is a request, not
// proof the order left the book (see kalshiCancelLifecycle), so an edit hides
// nothing on its own: like the fence, the id is tombstoned only when an
// admitted snapshot first omits it. Oldest-first, capped, cleared on disconnect.
const _editRetiring = new Set<string>();

function noteEditRetiring(orderId: string): void {
  _editRetiring.delete(orderId); // re-insert so Set order stays oldest-first
  _editRetiring.add(orderId);
  if (_editRetiring.size > KALSHI_CANCEL_TOMBSTONE_MAX) {
    _editRetiring.delete(_editRetiring.values().next().value as string);
  }
}

/** Tombstone every edit-retired id `orders` (an admitted snapshot) omits. */
function confirmEditRetirements(orders: KalshiOrder[], asOf: number | null): void {
  if (_editRetiring.size === 0) return;
  const listed = new Set(orders.map((order) => order.orderId));
  const confirmed = [..._editRetiring].filter((orderId) => !listed.has(orderId));
  for (const orderId of confirmed) _editRetiring.delete(orderId);
  _cancelTombstones = addKalshiCancelTombstones(_cancelTombstones, confirmed, asOf, Date.now(), 'edit');
}

function forgetCancelTombstone(orderId: string): void {
  if (!_cancelTombstones.has(orderId)) return;
  const next = new Map(_cancelTombstones);
  next.delete(orderId);
  _cancelTombstones = next;
}

/** Parse the relay's `ordersAsOf` stamp; null when absent or unusable. */
export function readOrdersAsOf(data: unknown): number | null {
  if (!data || typeof data !== 'object') return null;
  const raw = (data as Record<string, unknown>).ordersAsOf;
  return typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? raw : null;
}

/** #971: the overlay lane (`useUserKalshiPOS`) reads THIS store's confirmed-
 *  cancel tombstones, so a cancel confirmed here cannot blink back on the
 *  ladder either. Read-only: relist detection and expiry stay with the
 *  store's own admission path. `asOf` is the snapshot's `ordersAsOf`. */
export function withoutKalshiCancelTombstoned<O extends { orderId: string }>(
  orders: O[],
  asOf: number | null,
): O[] {
  const tombstones = _cancelTombstones;
  if (tombstones.size === 0) return orders;
  const nowMs = Date.now();
  const hidden = (order: O) => {
    const tomb = tombstones.get(order.orderId);
    return tomb !== undefined && kalshiTombstoneHides(tomb, asOf, nowMs);
  };
  return orders.some(hidden) ? orders.filter((order) => !hidden(order)) : orders;
}

/** Filter a validated, non-superseded snapshot through the tombstones and
 *  reconcile the fence against what remains. One call per write site, folded
 *  into the same `set` as openOrders. */
function admitOrdersSnapshot(
  states: KalshiCancelStates,
  orders: KalshiOrder[],
  asOf: number | null,
): { orders: KalshiOrder[]; kalshiCancelStates: KalshiCancelStates } {
  const nowMs = Date.now();
  const filtered = applyKalshiCancelTombstones(_cancelTombstones, orders, asOf, nowMs);
  _cancelTombstones = filtered.tombstones;
  confirmEditRetirements(orders, asOf);
  const reconciled = reconcileCancelFence(states, filtered.orders, asOf);
  return {
    orders: filtered.orders,
    kalshiCancelStates: markKalshiCancelRelisted(reconciled, filtered.relisted, nowMs),
  };
}

/** Reconcile the fence against a snapshot that has ALREADY been validated by
 *  parseOrdersSnapshot, and drop the machinery for every ID it retires. Callers
 *  fold the result into the same `set` that writes openOrders, so the list and
 *  the fence can never disagree for a frame. */
function reconcileCancelFence(
  states: KalshiCancelStates,
  orders: KalshiOrder[],
  asOf: number | null,
): KalshiCancelStates {
  const next = reconcileKalshiCancels(states, orders.map((order) => order.orderId));
  if (next === states) return states;
  const confirmed: string[] = [];
  for (const orderId of Object.keys(states)) {
    if (next[orderId] === undefined) {
      forgetCancelAttempt(orderId);
      confirmed.push(orderId);
    }
  }
  // #968: remember the confirmation so an older snapshot cannot re-admit it.
  _cancelTombstones = addKalshiCancelTombstones(_cancelTombstones, confirmed, asOf, Date.now());
  return next;
}

// ── speed-demon Deploy C: order-write HTTP timeout ──────────────────────────
// The relay's own venue timeout is 5 s; a browser-side 8 s bound guarantees
// every order write settles (the fetch can otherwise hang for minutes behind a
// stalled proxy, leaving the ticket's in-flight fence up). The signal is built
// on AbortController + setTimeout rather than AbortSignal.timeout() so the
// clock is the page's (fake-timer-able in tests) — the abort reason is the
// same 'TimeoutError' DOMException the native helper raises. NEVER retried:
// the venue may hold the order.
export const ORDER_HTTP_TIMEOUT_MS = 8_000;

export class OrderHttpTimeoutError extends Error {
  readonly code = 'ORDER_HTTP_TIMEOUT';
  constructor(op: string) {
    super(
      `${op} request timed out after ${ORDER_HTTP_TIMEOUT_MS / 1000}s — the relay did not answer. ` +
        'Check Open Orders before trying again.',
    );
    this.name = 'OrderHttpTimeoutError';
  }
}

/**
 * Speed-demon D1 — the replace route's cancel leg ended AMBIGUOUS (5xx / 408 /
 * transport failure on the venue DELETE): the original may or may not still
 * rest, and the relay placed nothing. Typed so callers ack it distinctly
 * ('unverified', never 'rejected') and NEVER retry — a retry could stack a
 * second order behind an original that is still live.
 */
export class ReplaceAmbiguousError extends Error {
  readonly code = 'REPLACE_AMBIGUOUS';
  constructor(message?: string) {
    super(
      message ??
        'Cancel outcome unknown — the original order may still rest. Check Open Orders before acting again.',
    );
    this.name = 'ReplaceAmbiguousError';
  }
}

/** The route the relay took for a replace (echoed from the response). */
export type ReplaceRoute = 'amend' | 'decrease' | 'cancel_place' | 'noop';

/** Options for `replaceOrder`. A bare boolean 4th argument is still accepted
 *  and means `forceCancelReplace` (the two edit modals pass it that way). */
export interface ReplaceOrderOptions {
  /** Bypass the smart route entirely: always cancel + place, even on a pure
   *  qty-shrink (the "Cycle" semantic). Takes today's cancel→place path, not
   *  the replace route, because the route smart-routes server-side. */
  forceCancelReplace?: boolean;
  /** Client order id for the replacement leg. The hotkey path passes a
   *  pooled `lt-` id (oneClickStore); absent, the store mints an `rp-` id
   *  inline. The modal edit is not latency-critical, and this module must
   *  not import the pool: it arms an idle refill timer at import, which the
   *  connect-retry contract forbids for this store. */
  clientOrderId?: string;
  /** Audit-only `__lt2` stamp (hotkey jumps). Stored, never on the wire. */
  auditMeta?: Record<string, unknown>;
  /** `__attack` marker — reserved; no jump today is an attack. */
  attackMeta?: Record<string, unknown>;
}

export interface ReplaceOrderResult {
  /** The id left resting: the original on noop/decrease/amend, the fresh
   *  id on cancel+place. */
  orderId: string;
  /** Which leg shape actually ran (absent on the local no-op and the
   *  legacy cancel→place fallback's decrease branch). */
  route?: ReplaceRoute | 'legacy_cancel_place';
}

interface OrderTimeoutSignal {
  signal: AbortSignal;
  /** Disarm the timer once the fetch settled (either way). */
  done: () => void;
}

function makeTimeoutReason(): unknown {
  const message = `Order request timed out after ${ORDER_HTTP_TIMEOUT_MS}ms`;
  if (typeof DOMException === 'function') return new DOMException(message, 'TimeoutError');
  const err = new Error(message);
  err.name = 'TimeoutError';
  return err;
}

function orderTimeoutSignal(): OrderTimeoutSignal {
  const controller = new AbortController();
  const handle = setTimeout(() => controller.abort(makeTimeoutReason()), ORDER_HTTP_TIMEOUT_MS);
  return { signal: controller.signal, done: () => clearTimeout(handle) };
}

/** True when a fetch rejected because OUR timeout signal fired (or the
 *  request was otherwise aborted) — distinct from a plain network failure. */
function isAbortLike(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const name = (err as { name?: unknown }).name;
  return name === 'TimeoutError' || name === 'AbortError';
}

/** Run an order-write fetch under the timeout bound. Rethrows a typed
 *  `OrderHttpTimeoutError` on abort; every other failure passes through. */
async function fetchOrderWrite(op: string, path: string, init: RequestInit): Promise<Response> {
  const timeout = orderTimeoutSignal();
  try {
    return await apiFetch(path, { ...init, signal: timeout.signal });
  } catch (err) {
    if (isAbortLike(err) || timeout.signal.aborted) throw new OrderHttpTimeoutError(op);
    throw err;
  } finally {
    timeout.done();
  }
}

// ── speed-demon Deploy C: post-write orders refresh gate ────────────────────
// With the relay push channel serving `orders`, a write already paints through
// the placement projection / cancel fence and the next push frame carries the
// authoritative list — the REST GET every write used to fire only added a
// full `set({openOrders})` re-render mid-order. `kickOrdersRefresh` keeps the
// immediate GET for the no-push case and, in push mode, arms ONE coalesced
// 3 s fallback that fetches only if no `orders` frame arrived since the
// latest kick (a silent stream after a write is the Jun 11 failure class —
// never trust the projection alone).
export const ORDERS_KICK_FALLBACK_MS = 3_000;
let _lastOrdersFrameAt = 0;
let _lastOrdersKickAt = 0;
let _ordersKickTimer: ReturnType<typeof setTimeout> | null = null;
let _ordersKickReason = '';

function clearOrdersKick(): void {
  if (_ordersKickTimer) clearTimeout(_ordersKickTimer);
  _ordersKickTimer = null;
}

function armOrdersKickFallback(delayMs: number): void {
  _ordersKickTimer = setTimeout(() => {
    _ordersKickTimer = null;
    if (_lastOrdersFrameAt >= _lastOrdersKickAt) return; // a frame covered the kick(s)
    const remaining = _lastOrdersKickAt + ORDERS_KICK_FALLBACK_MS - Date.now();
    if (remaining > 0) {
      // A later kick extended the window — give it its full grace.
      armOrdersKickFallback(remaining);
      return;
    }
    // Visible on purpose: a silent stream right after a write is the Jun 11
    // failure class, and the fallback read is the guard against it.
    console.warn(
      `[accountStore] ${new Date().toISOString()} orders push silent ${ORDERS_KICK_FALLBACK_MS}ms after ${_ordersKickReason}; REST fallback`,
    );
    useAccountStore.getState().refreshOrders().catch(() => {});
  }, delayMs);
}

export function kickOrdersRefresh(reason: string): void {
  if (!topicActive('orders')) {
    useAccountStore.getState().refreshOrders().catch(() => {});
    return;
  }
  _lastOrdersKickAt = Date.now();
  _ordersKickReason = reason;
  if (_ordersKickTimer) return; // coalesced — the armed fallback re-checks the latest kick
  armOrdersKickFallback(ORDERS_KICK_FALLBACK_MS);
}

// ── speed-demon Deploy C: per-fill positions refresh debounce ───────────────
// A fill burst (a sweep, a taker walking the book) used to fire one
// GET /positions per fill; the trailing-edge debounce collapses a burst to one
// fetch that sees every fill in it.
export const POSITIONS_FILL_DEBOUNCE_MS = 250;
let _positionsFillTimer: ReturnType<typeof setTimeout> | null = null;

function schedulePositionsRefreshForFill(): void {
  if (_positionsFillTimer) clearTimeout(_positionsFillTimer);
  _positionsFillTimer = setTimeout(() => {
    _positionsFillTimer = null;
    useAccountStore.getState().refreshPositions().catch(() => {});
  }, POSITIONS_FILL_DEBOUNCE_MS);
}

function clearPositionsFillDebounce(): void {
  if (_positionsFillTimer) clearTimeout(_positionsFillTimer);
  _positionsFillTimer = null;
}

/** The bare DELETE — no fence, no refresh. Split out for #272: a batch must be
 *  able to send N deletes and then refresh ONCE, and the per-ID refresh below
 *  would otherwise fire N times against the same org snapshot. */
async function deleteKalshiOrder(orderId: string): Promise<void> {
  // speed-demon T-A3: cancels compete with the feed's fetch storm — tell the
  // browser's scheduler this one is money-path.
  const res = await fetchOrderWrite('Cancel', `/api/kalshi/orders/${orderId}`, {
    method: 'DELETE',
    priority: 'high',
  });
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error((errData as Record<string, string>).error ?? `Cancel failed (${res.status})`);
  }
}

/** The raw cancel leg — DELETE, surface the venue's real error, kick the
 *  fire-and-forget refresh. NO fence: `replaceOrder`'s cancel-first edit path
 *  calls this directly so a price edit can't disable the row it is editing or
 *  raise the cancel-timeout notice. Only the public `cancelOrder`/`cancelOrders`
 *  fence. */
async function requestKalshiCancel(orderId: string): Promise<void> {
  await deleteKalshiOrder(orderId);
  forgetPlacementConfirmation(orderId);
  kickOrdersRefresh('cancel');
}

const CANCEL_ALREADY_PENDING_MESSAGE =
  'Cancel already pending; waiting for the authoritative Open Orders list.';

/** Health of the open-orders refresh loop. Surfaced on the positions page
 *  so a silently failing/stale order list is VISIBLE — the Jun 11 incident:
 *  a trader's orders were resting at Kalshi (intent tracker proved it) while
 *  the list sat empty with zero feedback, so he re-placed the same order 4x
 *  and stacked real exposure. Never fail this loop silently again. */
/**
 * Speed-demon D2 (260909) — the Spacebar pull rides `cancelOrdersByTickers`.
 * Absent (the `P` / usePullGame call) the request body is exactly `{tickers}`
 * and the behaviour is byte-identical to before.
 */
export interface CancelByTickersOptions {
  /** `live_pull_all` = the V3 Spacebar pull: the relay performs the kill-
   *  boundary authorization (LT3_PULL_ALL + LT2 grant, DURABLE attempt row
   *  before any venue write) and refuses with a non-2xx BEFORE anything is
   *  sent — surfaced here as a thrown `PullAllRefusedError`. */
  surface?: 'live_pull_all';
  /** Client-minted UUID v4 correlating the attempt row + per-order audits. */
  attemptId?: string;
  /** The frozen snapshot's id (live_pull_all: required by the relay). */
  snapshotId?: string;
  /** The FROZEN target ids: when supplied they are the local claim set (not
   *  a fresh scan of openOrders) and the relay cancels only these, intersected
   *  with the trader's own resting orders on `tickers`. */
  targets?: readonly string[];
  /** Old-relay / network fallback (the per-id DELETE path): run BEFORE the
   *  first DELETE; a throw refuses the whole fallback (`PullAllRefusedError`).
   *  The pull passes `recordPullAllAttempt` so the kill boundary survives a
   *  relay that predates the merged route. */
  authorizeFallback?: () => Promise<void>;
  /** Default true. `false` hands the post-write refresh to a caller that
   *  refreshes each venue once itself (the bulk executor). */
  refreshAfter?: boolean;
}

export interface OrdersSyncState {
  lastSuccessMs: number | null;
  lastErrorMs: number | null;
  lastError: string | null;
  consecutiveFailures: number;
}

/** Positions snapshot evidence, tracked SEPARATELY from the socket status
 *  (#881 review F4). `status === 'connected'` holds while every positions read
 *  fails or arrives malformed, and a retry-ladder `'connecting'` says nothing
 *  about the holdings already in hand. `synced` is true once a READABLE
 *  snapshot (including a valid empty one) has been applied this session and
 *  latches until disconnect; a failed or unreadable read keeps the last-good
 *  list and records the failure here instead of blanking the account or
 *  presenting an empty list as a known-flat account. The `*Ms` fields are
 *  local receipt evidence (`Date.now`), not a source clock and not a
 *  freshness deadline; no consumer may withhold on their age without a
 *  documented clock-domain-safe basis. */
export interface PositionsSyncState {
  synced: boolean;
  lastSuccessMs: number | null;
  lastErrorMs: number | null;
  lastError: string | null;
  consecutiveFailures: number;
}

const UNSYNCED_POSITIONS: PositionsSyncState = {
  synced: false, lastSuccessMs: null, lastErrorMs: null, lastError: null, consecutiveFailures: 0,
};

interface AccountState {
  status: AccountStatus;
  error: string | null;
  ordersSync: OrdersSyncState;
  /** Fills read health (#1025): failed or rejected reads, REST or push. Never carries a price. */
  fillsSync: OrdersSyncState;
  /** Authority of the last readable orders snapshot; close arithmetic only. */
  ordersScopeKey: string | null;
  positionsSync: PositionsSyncState;
  keyHealth: KeyHealth[];
  credentialCount: number;
  /** Whether `credentialCount` reflects a completed key-mode read for the
   *  current identity: `unknown` before the first read, `error` when the read
   *  failed. Only `checked` with a zero count means "no keys" — the others
   *  must not read as absence. A deliberate disconnect keeps the answer (the
   *  keys did not change); `resetCredentialCheck` clears it for a new identity. */
  credentialsStatus: 'unknown' | 'checked' | 'error';
  /** #1060: the last connect failure, kept while the store's own retries run
   *  (each attempt starts `connecting` with `error` cleared) so a failed
   *  connection does not read as a fresh connect between attempts. Cleared by
   *  a successful connect, a connect that finds no usable keys, a disconnect
   *  and a new identity's key check. */
  connectFailure: string | null;
  /** #1060: a connection is wanted (connect() requested, not since
   *  disconnected): mirrors the self-heal watchdog's own flag, so a read
   *  surface can tell a deliberate disconnect from a session the watchdog is
   *  about to reconnect (the stale-generation strand). */
  connectWanted: boolean;
  keyMode: 'org' | 'user' | null;
  /** The same key-mode read's answer for display (KeyModeBanner). Unlike
   *  `keyMode`, which follows the live connection, it survives a disconnect. */
  keyModeInfo: { mode: 'org' | 'user' | null; useOrgKeys: boolean; orgKeysAvailable: boolean } | null;
  /** Forget the key-mode answer: the identity it described is gone. */
  resetCredentialCheck: () => void;

  balance: number | null;
  /** #1205: the relay answered ORG_BALANCE_HIDDEN — a manager hid the
   *  organization balance from this trader. `balance` is null alongside it;
   *  readouts show the named state, never "—", $0 or a connection problem. */
  balanceHidden: boolean;
  positions: KalshiPosition[];
  openOrders: KalshiOrder[];
  /** #241 — per-order cancel fence, keyed by order ID. A row is disabled while
   *  its entry is pending; the entry only clears when a successfully parsed
   *  authoritative snapshot stops listing the ID (or the 5,000 ms sync timeout
   *  hands the control back). Kalshi-only; Polymarket cancels are hook-local. */
  kalshiCancelStates: KalshiCancelStates;
  /** #769 — the retained Kalshi slice, ALWAYS newest-first by `createdAt` and
   *  capped at MAX_RECENT_FILLS, whatever order the payload arrived in. The
   *  invariant is enforced in `processFill` (via `retainNewestFills`) rather
   *  than at the batch boundary, so it holds for a single live fill too. Order
   *  here is a contract, not an accident of insertion: it used to be the
   *  latter, and a newest-first batch then evicted the newest fills. */
  recentFills: KalshiFill[];
  /** #769 — the newest RECENT_FILL_LIMIT of `recentFills`, same ordering
   *  contract. A plain prefix of that list, never an independently built one. */
  activityTicker: KalshiFill[];
  /** #499 — true once a VALIDATED fills snapshot succeeded this session. The
   *  connection status cannot carry this: `status === 'connected'` holds even
   *  while every fills read fails or arrives malformed, so it must never back
   *  a "No recent fills" claim. Latches until disconnect, matching the
   *  retained-rows philosophy of the combined tape. */
  fillsSynced: boolean;
  realizedPnlCents: number;
  realizedFeesCents: number | null;
  realizedPnlNetCents: number | null;
  openNotionalCents: number;

  connect: () => Promise<void>;
  disconnect: () => void;
  /** Resolves true only when it FRESHLY set positions this call (connected,
   *  same connection generation, HTTP 200) — false on any no-op/stale path so a
   *  caller can tell "confirmed flat" from "couldn't read". */
  refreshPositions: () => Promise<boolean>;
  /** One actual REST positions read; callers should use refreshPositions. */
  readPositionsOnce: () => Promise<boolean>;
  refreshOrders: () => Promise<void>;
  refreshPnl: () => Promise<void>;
  refreshFills: () => Promise<void>;
  processFill: (fill: KalshiFill, clock?: RelayClock | null) => void;
  /** The ONE entry point for a multi-fill payload (#769). Both the REST
   *  bootstrap and the relay push `fills` topic go through it, so neither can
   *  drift into ingesting a batch in payload order. */
  ingestFillBatch: (fills: readonly KalshiFill[], clock?: RelayClock | null) => void;
  placeOrder: (input: OrderInput) => Promise<PlacedKalshiOrder>;
  /** Oscar overtake/blocker grouped sequence. Resolves with per-leg statuses
   *  (HTTP 200 once execution begins — check result.ok/legs); throws on
   *  pre-flight rejections (validation, permission, limit envelope). */
  placeOrderSequence: (input: OscarSequenceInput) => Promise<OscarSequenceResult>;
  /** Market-close a single (ticker, side) leg with verify-and-retry. Each
   *  attempt fires a reduce_only marketable-IOC sell (type:'market' → IOC at
   *  the 1¢ slippage floor) and reads the residual off the order response. The
   *  IOC cancels its unfilled remainder, so `requested − fill_count` is the
   *  authoritative residual; we re-sweep ONLY what's left, up to
   *  MAX_CLOSE_ATTEMPTS, then surface it. Resolves to 0 (confirmed flat), >0
   *  (exposure still open after exhausting attempts — caller must warn loudly),
   *  or null (the response carried no fill counts, opaque — do NOT blind-retry).
   *  Rejects if a POST itself fails (network / limit / API error). */
  sweepCloseLeg: (ticker: string, side: 'yes' | 'no', quantity: number) => Promise<number | null>;
  cancelOrder: (orderId: string) => Promise<void>;
  /** #272 — batch cancel riding the SAME #241 fence. Claims every ID in one
   *  synchronous write before the first DELETE, dedupes, runs the venue's
   *  requests sequentially, and refreshes ONCE (pass `refreshAfter: false` when
   *  a mixed-venue caller owns the per-venue refresh). Never throws for an
   *  individual order — the per-ID result carries `submitted` / `failed` /
   *  `already-pending` plus the venue's raw error, so a partial failure stays
   *  visible and retryable instead of collapsing into one message. */
  cancelOrders: (
    orderIds: readonly string[],
    options?: { refreshAfter?: boolean },
  ) => Promise<readonly KalshiCancelBatchResult[]>;
  /** Game-wide PULL fast path: ONE relay round trip
   *  (`POST /api/kalshi/orders/cancel-sequence`) cancels every resting order
   *  the caller has on the given tickers — the relay derives the target set
   *  itself and fans out the venue DELETEs with no client pacing. Rides the
   *  same #241 fence as `cancelOrders` (locally-known ids claimed in one
   *  synchronous write before the POST). A 404 from an older relay releases
   *  the claims and falls back to the sequential `cancelOrders` path —
   *  byte-identical old behavior. */
  cancelOrdersByTickers: (
    tickers: readonly string[],
    options?: CancelByTickersOptions,
  ) => Promise<readonly KalshiCancelBatchResult[]>;
  /** Queue-preserving partial cancel via the relay's /orders/:id/decrease
   *  proxy. Keeps the order's orderId + FIFO position; only the remaining
   *  count shrinks. `nextQuantity` must be an integer in [1, currentRemaining).
   *  Throws on validation or relay errors. Used by `replaceOrder`'s smart
   *  route when only qty shrinks at the same price. */
  decreaseOrder: (orderId: string, nextQuantity: number) => Promise<void>;
  /** Cancel + re-place an open Kalshi order at a new price and/or quantity.
   *  Smart-routes by default: when only the quantity shrinks (same price,
   *  smaller remaining), calls `decreaseOrder` instead — preserves queue
   *  position. Otherwise (price moved, qty increased, both changed) cancel
   *  + place, preserving ticker/side/action/expirationTs. Pass
   *  `forceCancelReplace: true` to bypass the smart route entirely —
   *  always cancel + place, even on a pure qty-shrink. Used when the
   *  trader explicitly wants to send the order to the back of the queue
   *  (the "Cycle" semantic). Throws with `'Could not cancel original
   *  order: …'` if the cancel leg fails, or `'Original order was cancelled,
   *  but the replacement failed to place: …'` if the place leg fails
   *  after the cancel. Resolves with the id of the order left resting:
   *  the original id on the no-op and /decrease routes, the freshly
   *  placed id on cancel+place — callers keying UI state off the order
   *  id (pinned cards) transfer on that. */
  replaceOrder: (
    orderId: string,
    newPriceCents: number,
    newQuantity?: number,
    opts?: boolean | ReplaceOrderOptions,
  ) => Promise<ReplaceOrderResult>;
  checkCredentials: () => Promise<void>;
}

/** #1025: a failed or rejected fills read leaves a read owed. While one is owed the
 *  fill poll runs even though the push topic is healthy, backing off (D11) so a
 *  failure that won't change isn't retried every few seconds. A GET of the fills
 *  list only: never an order action. */
let _fillsReadOwed = false;
/** Durations are `performance.now()` readings (clock-domain-safety.md): a wall-clock step
 *  must not stretch or shrink a backoff or the in-flight hold. Epoch time stays in fillsSync only. */
let _fillsReadDueAtMono = 0;
let _fillsRestFailures = 0;
/** Counts every failed or rejected read (REST or push). A good read clears the owed
 *  flag only if no failure arrived after that read was sent (F1: a frame rejected
 *  while a read was in flight needs a read that started after it). */
let _fillsFailureSeq = 0;
let _fillsReadsInFlight = 0;
let _fillsLastSendMono = 0;
/** The read scope (`historicalReadScope`) the pending fills work belongs to. If it changes
 *  without a reconnect (key-mode or role switch, new session), the work is retired. */
let _fillsWorkScope: string | null = null;
/** Bumped on every reset: a settling read decrements the in-flight hold only if it
 *  started in the current epoch, so a retired read cannot release a newer one's hold. */
let _fillsReadEpoch = 0;
const FILLS_REARM_BACKOFF_MS = [5_000, 10_000, 20_000, 30_000];

/** A due time more than one backoff cap away, or a send stamp in the future, can only be a
 *  restarted monotonic origin (a test harness; a document's clock never runs backward): read now. */
function fillsReadHeldBack(pushHealthy: boolean): boolean {
  const now = performance.now();
  const cap = FILLS_REARM_BACKOFF_MS[FILLS_REARM_BACKOFF_MS.length - 1];
  if (_fillsReadDueAtMono - now > cap) _fillsReadDueAtMono = now;
  // A read that never settles (no fetch timeout) holds the owed read for at most one cap.
  const heldFor = now - _fillsLastSendMono;
  if (_fillsReadsInFlight > 0 && (heldFor >= cap || heldFor < 0)) {
    // Expired: forget the hung read, so it cannot hold every later read as well.
    _fillsReadEpoch += 1;
    _fillsReadsInFlight = 0;
    // The forgotten read is fenced if it ever lands, so a read must still be owed: the replacement
    // may hang too, and without this the poll could stand down with nothing read.
    _fillsReadOwed = true; _fillsWorkScope = historicalReadScope();
  }
  // One in-flight read in both modes; the backoff due time only while push is healthy (#843 keeps its cadence).
  return _fillsReadsInFlight > 0 || (pushHealthy && now < _fillsReadDueAtMono);
}

function resetFillsReadOwed() {
  _fillsReadEpoch += 1;
  _fillsWorkScope = null;
  _fillsFailureSeq += 1;
  _fillsReadsInFlight = 0;
  _fillsReadOwed = false;
  _fillsReadDueAtMono = 0;
  _fillsRestFailures = 0;
}

/** Retire owed or in-flight fills work that belongs to an older read scope (same
 *  connection, but the user, org, role, key mode or session changed under it). Its
 *  streak and error text go with it. Returns true when it retired something. */
function retireStaleFillsWork(): boolean {
  if (_fillsWorkScope === null || _fillsWorkScope === historicalReadScope()) return false;
  resetFillsReadOwed();
  useAccountStore.setState({ fillsSync: { lastSuccessMs: null, lastErrorMs: null, lastError: null, consecutiveFailures: 0 } });
  reconcilePolling(false);
  return true;
}

function stopPolling() {
  resetFillsReadOwed();
  if (_stopPollTimer) { _stopPollTimer(); _stopPollTimer = null; }
  if (_stopFillPollTimer) { _stopFillPollTimer(); _stopFillPollTimer = null; }
}

function corePushActive(): boolean {
  return (
    topicActive('positions') &&
    topicActive('orders') &&
    topicActive('fills') &&
    topicActive('pnl')
  );
}

let _lastPollFn: (() => void) | null = null;
let _lastFillPollFn: (() => void) | null = null;

/** Preserve the normal account backstop, but recover fills independently.
 * A hidden 30s poll can discover a fill only after its strict 30s sound
 * admission window. Ten seconds leaves room for ingestion/transport delay;
 * it does not extend recency or replay history. Healthy fill push stops this
 * timer even when some other account topic still needs its fallback.
 */
function reconcilePolling(immediate: boolean) {
  if (corePushActive()) {
    if (_stopPollTimer) { _stopPollTimer(); _stopPollTimer = null; }
  } else if (!_stopPollTimer && _lastPollFn) {
    if (immediate) _lastPollFn();
    _stopPollTimer = startVisibilityAwareInterval(_lastPollFn, POLL_INTERVAL_MS);
  }
  if (topicActive('fills') && !_fillsReadOwed) {
    if (_stopFillPollTimer) { _stopFillPollTimer(); _stopFillPollTimer = null; }
  } else if (!_stopFillPollTimer && _lastFillPollFn) {
    if (immediate) _lastFillPollFn();
    _stopFillPollTimer = startVisibilityAwareInterval(_lastFillPollFn, POLL_INTERVAL_MS, 2);
  }
}

function startPolling(poll: () => void, pollFills: () => void) {
  stopPolling();
  _lastPollFn = poll;
  _lastFillPollFn = pollFills;
  poll(); // initial snapshots still heal reconnect gaps
  pollFills();
  reconcilePolling(false);
}

onActiveChange(() => {
  if (!_lastPollFn || useAccountStore.getState().status !== 'connected') return;
  reconcilePolling(true);
});

function nextConnectionGeneration() {
  _connectionGeneration += 1;
  return _connectionGeneration;
}

function isCurrentConnectionGeneration(generation: number) {
  return _connectionGeneration === generation;
}

/** One sequenced read of the Kalshi balance after connect: the 5 s poll's, and
 *  (#1205) the push balance topic's when a visible frame finds no figure on
 *  screen. Only the newest answer applies. `visibleKnown`: the relay has just said the
 *  balance is visible, so if this read then fails, "Hidden" is no longer true
 *  and the readout falls back to the ordinary unreadable state ("—"). */
function readPolledBalance(generation: number, visibleKnown = false): void {
  const balanceSeq = ++_balanceReadSeq;
  apiFetch('/api/kalshi/balance').then(async (r) => {
    const d = await r.json().catch(() => ({})) as Record<string, unknown>;
    const st = useAccountStore.getState();
    if (!isCurrentConnectionGeneration(generation) || st.status !== 'connected') return;
    if (balanceSeq < _balanceAppliedSeq) return; // a newer answer already applied
    if (r.ok) {
      _balanceAppliedSeq = balanceSeq;
      // Org scope carries the real account balance (relay unmasked Jul-8)
      // unless a manager hid it (#1205, below); an old relay's placeholder has
      // no balance fields and leaves the stored value untouched.
      useAccountStore.setState({ balance: pickNum(d.balance, d.available_balance) ?? st.balance, balanceHidden: false });
    } else if (isOrgBalanceHiddenBody(d)) {
      // #1205: hidden clears the figure; any other failure keeps the figure.
      _balanceAppliedSeq = balanceSeq;
      useAccountStore.setState({ balance: null, balanceHidden: true });
    } else if (visibleKnown && st.balanceHidden) {
      _balanceAppliedSeq = balanceSeq;
      useAccountStore.setState({ balanceHidden: false });
    }
  }).catch(() => {
    const st = useAccountStore.getState();
    // `<=`: an exception thrown after this same read applied must not undo it.
    if (!visibleKnown || !st.balanceHidden || balanceSeq <= _balanceAppliedSeq) return;
    if (!isCurrentConnectionGeneration(generation) || st.status !== 'connected') return;
    _balanceAppliedSeq = balanceSeq;
    useAccountStore.setState({ balanceHidden: false });
  });
}

// Starts the self-heal watchdog once (idempotent). Gated to do no harm — it
// only nudges connect() back to life on a provably-stranded session:
//   • _desiredConnected — a connect() was requested and not since disconnected,
//     so it stays silent after logout/disconnect (which set it false).
//   • !_connectInFlight && !_connectRetryTimer — nothing is already trying. The
//     retry ladder owns the 429-backoff path and keeps a timer alive even in
//     'error'; we never compete with it. Among credentialed sessions, the only
//     state with no in-flight + no timer + not connected is the stale-generation
//     strand (the keyless canTrade:false rest state also matches the timer
//     conditions but is excluded by the credentialCount guard below).
//   • credentialCount > 0 — the trader has keys (no creds → no reconnect churn).
//   • status !== 'connected' — only act on a stranded session, never churn a
//     healthy one.
function ensureHealWatchdog(getState: () => AccountState) {
  if (_healWatchdog) return;
  _healWatchdog = setInterval(() => {
    if (!_desiredConnected) return;
    if (_connectInFlight || _connectRetryTimer) return;
    const st = getState();
    if (st.credentialCount <= 0) return;
    if (st.status === 'connected') return;
    void st.connect();
  }, HEAL_WATCHDOG_INTERVAL_MS);
}

function pickStr(...c: unknown[]) { for (const v of c) if (typeof v === 'string' && v !== '') return v; return undefined; }
function pickNum(...c: unknown[]) { for (const v of c) { if (typeof v === 'number' && !isNaN(v)) return v; if (typeof v === 'string' && v !== '') { const n = parseFloat(v); if (!isNaN(n)) return n; } } return undefined; }
function pickWholeContracts(...c: unknown[]) {
  const n = pickNum(...c);
  return n == null ? undefined : Math.max(0, Math.round(n));
}

/** A definite refusal: the relay or the venue answered and the order was not
 *  placed. `code` is the relay's machine code when it sent one (for example
 *  ONE_CLICK_NOT_ENABLED or ATTACK_NOT_ENABLED), so a caller can react to a
 *  capability change without parsing the sentence. Undefined when the relay
 *  sent none. */
/** What `placeOrder` resolves with. `selfTradeCancel` is present only when
 *  Kalshi's self-trade prevention cancelled the unfilled remainder after a
 *  partial fill (#1229): the fills are real, nothing rests, and the caller
 *  shows the amber line instead of "Order placed". A zero-fill cancel never
 *  resolves; it throws `SelfTradeCancelledError`. */
export interface PlacedKalshiOrder extends KalshiOrder {
  selfTradeCancel?: SelfTradeCancel;
}

export class OrderRefusedError extends Error {
  readonly code: string | undefined;

  constructor(message: string, code?: string) {
    super(message);
    this.name = 'OrderRefusedError';
    this.code = code;
  }
}

function relayCode(errData: unknown): string | undefined {
  const code = (errData as Record<string, unknown> | null)?.code;
  return typeof code === 'string' && code !== '' ? code : undefined;
}

/** An edit's post-only replacement priced through the book. Refused before
 *  the cancel, so the trader's resting order is untouched. */
export const EDIT_WOULD_CROSS_MESSAGE =
  'Price would cross the book; the post-only replacement was not sent. Original kept.';

/** An edit whose cancel succeeded and whose replacement's result is unknown. */
export const EDIT_REPLACEMENT_UNKNOWN_MESSAGE =
  'Original order was cancelled. Replacement result unknown, may be live; check open orders before placing again.';

/**
 * Whether a post-only limit at `priceCents` would take liquidity on the live
 * book for `ticker` (a Kalshi ticker or a Polymarket market ticker). `side`
 * and `action` are trader-native: a SELL of YES at p crosses when the best YES
 * bid is at or above p, which is a BUY of NO at 100 − p.
 *
 * Null when no usable book is held (missing, withdrawn or unconfirmed): an
 * unknown book is not a refusal, and the venue still enforces post-only.
 */
export function postOnlyWouldCross(
  ticker: string,
  side: 'yes' | 'no',
  action: 'buy' | 'sell',
  priceCents: number,
): boolean | null {
  const store = useMarketStore.getState();
  const raw = store.getOrderbookRaw(ticker);
  if (!raw) return null;
  const book = {
    marketTicker: ticker,
    yes: raw.yes.map((level) => ({ priceCents: level.price, size: level.size })),
    no: raw.no.map((level) => ({ priceCents: level.price, size: level.size })),
    lastPriceCents: store.getLastTradeYesCents(ticker),
  } as unknown as VenueBook;
  const role =
    action === 'buy'
      ? limitOrderRole(book, side, priceCents)
      : limitOrderRole(book, side === 'yes' ? 'no' : 'yes', mirrorCents(priceCents));
  return role == null ? null : role === 'taker';
}

/** Runs an edit and rings the rejection tone for any refusal that happens
 *  before its replacement is placed (validation, the live-book and identity
 *  checks, a failed cancel or decrease). Once the edit calls `markPlacing`,
 *  placeOrder owns the tones, and rings nothing for an unknown result. */
async function announcingEditRefusals<T>(run: (markPlacing: (placing?: boolean) => void) => Promise<T>): Promise<T> {
  let placing = false;
  try {
    // streamrift: the relay replace route may cancel and place during one
    // request, so the edit marks "placing" before that request; a 404 from an
    // older relay proves nothing ran and un-marks it for the browser fallback.
    return await run((next = true) => { placing = next; });
  } catch (err) {
    if (!placing) {
      const snd = readSoundSettings();
      if (snd.orderRejectionSoundEnabled) playOrderRejectedTone(getChannelVolume('orderRejected'));
    }
    throw err;
  }
}

/** The words an unknown LIMIT-order result carries. A resting limit order may
 *  be live, so the one instruction is to look before placing again. A complete
 *  sentence: a modal or result line prints it on its own, and the one-click
 *  strip prints it after the order's label. Market (IOC) orders keep the
 *  generic sentence: nothing rests. */
export const UNKNOWN_LIMIT_RESULT_MESSAGE =
  'Order result unknown; it may be live. Check open orders before placing again.';

function unknownSubmission(input: OrderInput): OrderSubmissionUnknownError {
  return input.type === 'limit' && input.timeInForce !== 'immediate_or_cancel'
    ? new OrderSubmissionUnknownError(UNKNOWN_LIMIT_RESULT_MESSAGE)
    : new OrderSubmissionUnknownError();
}

/**
 * POST an order to the relay and return the RAW Kalshi order object
 * (`data.order ?? data`) — the unmapped response, so callers can read fill /
 * remaining counts off it. Throws a typed `LimitRejectionError` on the
 * structured limit-rejection envelope, else a generic Error. No sounds, no
 * refresh — the caller owns those side effects.
 */
async function postOrderRaw(input: OrderInput): Promise<Record<string, unknown>> {
  assertSourceConfirmed({ ticker: input.ticker });
  // `input.exchangeIndex` is observation context, not write authority. #575
  // owns the eventual exchange_index=-1 venue contract; this explicit builder
  // intentionally leaves the observed value off today's relay wire.
  const body: Record<string, unknown> = {
    ticker: input.ticker,
    side: input.side,
    action: input.action,
    type: input.type,
    count: input.count,
    count_fp: String(input.count),
  };
  if (input.price !== undefined) {
    // 4 dp carries the 0.01¢ grain exactly (sub-cent deci_cent markets);
    // toFixed(2) silently quantized to whole cents.
    body[`${input.side}_price_dollars`] = (input.price / 100).toFixed(4);
  }
  if (input.postOnly) body.post_only = true;
  if (input.timeInForce) body.time_in_force = input.timeInForce;
  if (input.expirationTime) body.expiration_ts = Math.floor(new Date(input.expirationTime).getTime() / 1000);
  // Good to Start audit stamp: stored in trades.request_payload for the
  // relay's start watcher; the relay's venue allowlist keeps it off Kalshi.
  if (input.expirationTime && input.goodToStart) {
    body.__gts = 'startMs' in input.goodToStart
      ? { v: 1, startMs: input.goodToStart.startMs }
      : { v: 1, carryFrom: input.goodToStart.carryFrom };
  }
  // reduce_only guards a close from over-filling past flat into an opposite
  // position (the relay translator forwards body.reduce_only into the V2 body).
  if (input.reduceOnly) body.reduce_only = true;
  if (input.clientOrderId) body.client_order_id = input.clientOrderId;
  if (input.deskResolution) body.__deskResolution = input.deskResolution;
  // Audit-only stamp: stored verbatim in trades.request_payload; the relay's
  // legacyToV2Order allowlist keeps it off the venue wire.
  if (input.auditMeta) body.__lt2 = input.auditMeta;
  // Attack gate marker (V3): the relay checks grant + ceiling + depth on the
  // key's presence; same venue-allowlist strip as __lt2.
  if (input.attackMeta) body.__attack = input.attackMeta;

  let res: Response;
  // speed-demon T-A3: the wire is about to open — the submit→request gap is
  // the client-side pre-send cost (fence resolution, body build, scheduling).
  probeMark('request');
  try {
    res = await fetchOrderWrite('Order', '/api/kalshi/orders', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      priority: 'high',
    });
  } catch (err) {
    // The browser lost the relay response after submission began. The same
    // client_order_id may already be live; never present this as a rejection.
    // A timeout (speed-demon Deploy C) is the same unknown-outcome class —
    // the venue may hold the order — so it stays an OrderSubmissionUnknownError
    // (one-click / ticket paths key 'unverified' off the instance) and carries
    // the timeout's message so the trader sees WHY it is unverified.
    if (err instanceof OrderHttpTimeoutError) throw new OrderSubmissionUnknownError(err.message);
    throw unknownSubmission(input);
  }
  // Attach the relay's per-stage Server-Timing split to the in-flight probe
  // sample (present only on ORDER_TIMING-enabled lanes). Guarded: tests mock
  // apiFetch with headerless response stubs.
  if (typeof res.headers?.get === 'function') {
    const stages = parseServerTiming(res.headers.get('server-timing'));
    if (stages) probeAttachServerTiming(stages);
  }
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    if(errData.code==='DESK_RESOLUTION_CONFIRM' && errData.plan) throw new DeskResolutionConfirmation(errData.plan,input);
    if (errData.code === 'DESK_RESOLUTION_STOPPED') {
      const receipts = Array.isArray(errData.receipts) ? errData.receipts as {id:string;state:string;quantity?:string}[] : [];
      const detail = receipts.map(r => `${r.id}: ${r.state === 'claimed' ? 'unconfirmed' : r.state}${r.quantity ? ` ${r.quantity} contracts` : ''}`).join('; ');
      throw new OrderRefusedError(`${errData.error}${detail ? ` Outcomes: ${detail}.` : ''}`, 'DESK_RESOLUTION_STOPPED');
    }
    // Structured limit-rejection envelope from relay/checkOrderLimit. Throw a
    // typed error so order forms can render richer UI than the generic toast
    // (breach details: rule, cap, current, requested).
    if (isLimitRejectionPayload(errData)) {
      throw new LimitRejectionError(errData.error, errData.code, errData.breach);
    }
    // This relay envelope is emitted before the placement handler starts.
    // Unlike a proxy 503, it proves that no venue submission took place.
    if ((errData as Record<string, unknown>).code === 'RELAY_DRAINING') {
      throw new OrderRefusedError(
        'Relay is restarting. This order was not sent; try again after reconnecting.',
        'RELAY_DRAINING',
      );
    }
    // The relay failed before any venue I/O (credentials, limit read): proven
    // not sent, so it is a definite rejection that keeps the relay's reason,
    // never an unknown result that would hold an S leg as unverified.
    if (relayCode(errData) === 'ORDER_NOT_SENT') {
      const reason = (errData as Record<string, unknown>).error;
      throw new OrderRefusedError(
        `not sent: ${typeof reason === 'string' && reason.trim() !== '' ? reason.trim() : 'the relay could not prepare this order'}`,
        'ORDER_NOT_SENT',
      );
    }
    // The relay's own evidence, whatever its words: the venue may hold the
    // order. The trader gets one instruction, not the transport detail.
    if ((errData as Record<string, unknown>).code === 'KALSHI_ORDER_STATE_UNKNOWN') {
      throw unknownSubmission(input);
    }
    // A front proxy can lose the relay's structured envelope during the same
    // ambiguous post-submit failure (for example, a restart returning an HTML
    // 502/504). The venue may still hold the client_order_id.
    if (res.status === 408 || res.status >= 500) {
      throw unknownSubmission(input);
    }
    throw new OrderRefusedError(
      (errData as Record<string, string>).error ?? `Order failed (${res.status})`,
      relayCode(errData),
    );
  }
  let data: Record<string, unknown>;
  try {
    data = (await res.json()) as Record<string, unknown>;
  } catch {
    // The relay reported success but the response was truncated before the
    // order identity arrived. Acceptance is possible; never invite a retry.
    throw unknownSubmission(input);
  }
  const order = (data.order ?? data) as Record<string, unknown>;
  // The relay's classification (#1229) sits at the top level beside the venue
  // body; keep it with the order whichever nesting the venue used.
  return order !== data && data.g1Outcome !== undefined && order.g1Outcome === undefined
    ? { ...order, g1Outcome: data.g1Outcome }
    : order;
}

function dollarsToCents(dollars: unknown, fallbackCents?: unknown): number {
  // 0.01¢ grain preserved — whole-cent rounding here fed rounded prices into
  // order display and PnL reads (sub-cent contract, 2026-07-17).
  if (typeof dollars === 'string' && dollars !== '') { const n = parseFloat(dollars); if (!isNaN(n)) return Math.round(n * 10000) / 100; }
  if (typeof dollars === 'number') return dollars <= 1.01 ? Math.round(dollars * 10000) / 100 : Math.round(dollars * 100) / 100;
  if (typeof fallbackCents === 'number') return Math.round(fallbackCents * 100) / 100;
  return 0;
}

// Like dollarsToCents but preserves centicent precision — Kalshi reports
// fees_paid_dollars as a 4-decimal string (e.g. "0.0734") and rounding to
// whole cents would silently drop sub-cent fee accruals.
function parseFeesPaidCents(dollars: unknown, fallbackCents?: unknown): number {
  if (typeof dollars === 'string' && dollars !== '') {
    const n = parseFloat(dollars);
    if (!isNaN(n)) return n * 100;
  }
  if (typeof dollars === 'number' && Number.isFinite(dollars)) return dollars * 100;
  if (typeof fallbackCents === 'number' && Number.isFinite(fallbackCents)) return fallbackCents;
  return 0;
}

// Exported so org-wide surfaces (e.g. the manager Org-Account view) parse raw
// Kalshi /portfolio/positions with the SAME field handling (position_fp,
// *_price_dollars, zero-qty filtering) instead of re-hand-rolling it.
type PositionRowRead =
  | { kind: 'position'; position: KalshiPosition }
  | { kind: 'flat' }
  | { kind: 'malformed'; reason: string };

const isYesNo = (v: unknown): v is 'yes' | 'no' => v === 'yes' || v === 'no';
const finiteOrUndefined = (v: number | undefined): boolean => v === undefined || Number.isFinite(v);

// Snapshot evidence must consume the whole numeric field. parseFloat-style
// prefix parsing can turn a corrupt "2contracts" into a confirmed holding.
function positionNumber(...values: unknown[]): number | undefined {
  const value = values.find((v) => v !== undefined && v !== null);
  if (typeof value !== 'number' && typeof value !== 'string') return undefined;
  if (typeof value === 'string' && value.trim() === '') return undefined;
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
}

/** One positions row, classified (#881 review F4 follow-up). The normalisation
 *  is byte-for-byte the previous `parsePositions` row mapping; what is new is
 *  that a row is only ACCEPTED when it carries recognisable identity and
 *  finite holding data, and only treated as legitimately FLAT when its count
 *  field is present, finite and zero/negative. Anything else is malformed, so
 *  a snapshot containing `{}` or a tickerless/countless row can no longer be
 *  applied as a smaller (or empty) account. Supported envelopes: relay V2
 *  (`netCount`) and raw Kalshi `market_positions` (`position`/`position_fp`,
 *  or `quantity`/`total_traded` with `side`). */
function readPositionRow(p: Record<string, unknown>): PositionRowRead {
  const malformed = (reason: string): PositionRowRead => ({ kind: 'malformed', reason });
  const ticker = pickStr(p.ticker, p.market_ticker);
  if (!ticker) return malformed('row without ticker');
  if (p.side !== undefined && p.side !== null && !isYesNo(p.side)) return malformed('row with unknown side');

  // V2 shape from /api/trader/positions: { ticker, side, netCount, avgEntryPriceCents, realizedPnlCents }
  if ('netCount' in p) {
    const position = positionNumber(p.netCount);
    if (position === undefined || !Number.isFinite(position)) return malformed('row without finite contract count');
    if (position <= 0) return { kind: 'flat' };
    const averagePrice = positionNumber(p.avgEntryPriceCents);
    const costBasisCents = positionNumber(p.costBasisCents);
    // Holdings evidence (identity, side, finite count) is what makes a row
    // readable. Entry-basis evidence is separate: a row with neither an
    // average price nor an exact cost basis is still a real holding (the
    // sweep-close residual read depends on it), so it is kept, and its P&L
    // basis is flagged unknown instead of silently costed at zero.
    const entryBasisUnknown = !Number.isFinite(averagePrice ?? NaN) && !Number.isFinite(costBasisCents ?? NaN);
    const realizedPnl = pickNum(p.realizedPnlCents);
    // Per-user fee sums from the relay's fills ledger; undefined when no
    // contributing fill carried a fee field, so the UI distinguishes
    // "unknown" from "actually $0.00".
    const feesPaidCents = pickNum(p.feesPaidCents);
    // Open-share entry fees — the unrealized-recipe fee input. The bucket
    // total above double-counts the sold share against realized-net on
    // partially-closed positions.
    const openFeesCents = pickNum(p.openFeesCents);
    // Full-position anchor (#91). Only the V2 relay path carries these —
    // the raw Kalshi branch below is a venue aggregate with no lifetime
    // sums, so cards must treat them as optional, not assume zero.
    const totalBoughtCount = pickNum(p.totalBoughtCount);
    const totalSoldCount = pickNum(p.totalSoldCount);
    const totalBuyCostCents = pickNum(p.totalBuyCostCents);
    if (![averagePrice, costBasisCents, realizedPnl, feesPaidCents, openFeesCents, totalBoughtCount, totalSoldCount, totalBuyCostCents].every(finiteOrUndefined)) {
      return malformed('row with non-finite field');
    }
    return {
      kind: 'position',
      position: {
        ticker,
        side: isYesNo(p.side) ? p.side : 'yes',
        position,
        averagePrice: averagePrice ?? 0,
        lastFillAt: pickStr(p.lastFillAt, p.last_fill_at) || null,
        costBasisCents,
        ...(entryBasisUnknown ? { entryBasisUnknown: true as const } : null),
        realizedPnl: realizedPnl ?? 0,
        feesPaidCents,
        openFeesCents,
        totalBoughtCount,
        totalSoldCount,
        totalBuyCostCents,
        // V58 origin attribution: present only when RFQ fills contributed.
        ...(p.origin === 'rfq' || p.origin === 'mixed' ? { origin: p.origin } : {}),
      },
    };
  }

  // Kalshi /portfolio/positions market_positions[] carries the signed
  // contract count as `position_fp` (fp string — "1.00" / "-1.00") and
  // does not include an integer `position` field on current responses.
  // Without position_fp here, every row resolved to position 0 and got
  // filtered out — /positions showed "No open positions" despite live
  // contracts. Positive = long YES, negative = short YES (= long NO).
  const rawPos = positionNumber(p.position, p.position_fp);
  let side: 'yes' | 'no';
  let position: number;
  if (rawPos !== undefined) {
    if (!Number.isFinite(rawPos)) return malformed('row without finite contract count');
    side = rawPos >= 0 ? 'yes' : 'no';
    position = Math.abs(rawPos);
  } else {
    if (p.position != null || p.position_fp != null) return malformed('row without finite contract count');
    const quantity = positionNumber(p.quantity, p.total_traded);
    if (quantity === undefined || !Number.isFinite(quantity)) return malformed('row without finite contract count');
    side = isYesNo(p.side) ? p.side : 'yes';
    position = quantity;
  }
  if (position === 0) return { kind: 'flat' };
  // Kalshi also omits `average_price_dollars` on market_positions — derive
  // the avg from total_traded_dollars / |position| (correct for buy-only
  // open positions; reasonable approximation for mixed buy/sell cases).
  // Legacy field-name fallback retained for older payload shapes.
  const totalTradedDollars = positionNumber(p.total_traded_dollars, p.total_traded);
  const avgFromTraded =
    totalTradedDollars != null && position > 0
      ? Math.round((totalTradedDollars / position) * 100)
      : null;
  const rawPriceDollars = p.average_price_dollars ?? p[`${side}_price_dollars`];
  const rawPriceCents = p.average_price ?? p[`${side}_price`];
  const hasPriceField = positionNumber(rawPriceDollars, rawPriceCents) !== undefined;
  // Same split as the V2 branch: no price source keeps the holding and
  // flags the basis unknown (the previous silent 0¢ average).
  const entryBasisUnknown = avgFromTraded === null && !hasPriceField;
  const avgPrice =
    avgFromTraded ??
    (hasPriceField ? dollarsToCents(rawPriceDollars, rawPriceCents) : 0);
  const realizedPnl = dollarsToCents(p.realized_pnl_dollars, p.realized_pnl);
  // Kalshi /portfolio/positions returns fees_paid_dollars (string, e.g.
  // "0.0734") with centicent precision. dollarsToCents Math.rounds to whole
  // cents, which would silently truncate sub-cent fees — so parse fees
  // directly and keep the fractional cents.
  const feesPaidCents =
    p.fees_paid_dollars !== undefined || p.fees_paid !== undefined
      ? parseFeesPaidCents(p.fees_paid_dollars, p.fees_paid)
      : undefined;
  if (![position, avgPrice, realizedPnl].every(Number.isFinite) || !finiteOrUndefined(feesPaidCents)) {
    return malformed('row with non-finite field');
  }
  return {
    kind: 'position',
    position: {
      ticker,
      side,
      position,
      averagePrice: avgPrice,
      lastFillAt: pickStr(p.lastFillAt, p.last_fill_at) || null,
      realizedPnl,
      feesPaidCents,
      ...(entryBasisUnknown ? { entryBasisUnknown: true as const } : null),
    },
  };
}

// Exported so org-wide surfaces (e.g. the manager Org-Account view) parse raw
// Kalshi /portfolio/positions with the SAME field handling (position_fp,
// *_price_dollars, zero-qty filtering) instead of re-hand-rolling it. Row
// filtering is PERMISSIVE here (flat and malformed rows are dropped); the
// account store reads through `readPositionsSnapshot`, which rejects a
// snapshot containing any malformed row.
export function parsePositions(data: Record<string, unknown>): KalshiPosition[] {
  const raw = (data.market_positions ?? data.positions ?? data.results ?? data.data ?? []) as unknown[];
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((p) => {
    if (!p || typeof p !== 'object' || Array.isArray(p)) return [];
    const read = readPositionRow(p as Record<string, unknown>);
    return read.kind === 'position' ? [read.position] : [];
  });
}

/** Readable-or-not positions snapshot (#881 review F4). Null means the payload
 *  is UNREADABLE (not an object, no recognised positions container, a
 *  container that is not an array, or a row that is not an object) and must
 *  never be applied as an empty account. A recognised empty container is a
 *  valid, authoritative empty snapshot. Row-level normalisation stays exactly
 *  `parsePositions`. */
export function parsePositionsSnapshot(data: unknown): KalshiPosition[] | null {
  const read = readPositionsSnapshot(data);
  return read.ok ? read.positions : null;
}

export type PositionsSnapshotRead =
  | { ok: true; positions: KalshiPosition[]; historicalIdentities?: unknown }
  | { ok: false; error: string };

/** Whole-snapshot rejection, not per-row filtering: one malformed row makes
 *  the snapshot unreadable, because a partially dropped account is
 *  indistinguishable from a complete one. Flat rows are readable and simply
 *  absent from the result. */
export function readPositionsSnapshot(data: unknown): PositionsSnapshotRead {
  let rows: unknown[];
  if (Array.isArray(data)) {
    rows = data;
  } else {
    if (!data || typeof data !== 'object') return { ok: false, error: 'malformed positions payload' };
    const o = data as Record<string, unknown>;
    const container = o.market_positions ?? o.positions ?? o.results ?? o.data;
    if (!Array.isArray(container)) return { ok: false, error: 'malformed positions payload' };
    rows = container;
  }
  const positions: KalshiPosition[] = [];
  for (const row of rows) {
    if (!row || typeof row !== 'object' || Array.isArray(row)) {
      return { ok: false, error: 'malformed positions payload (non-object row)' };
    }
    const read = readPositionRow(row as Record<string, unknown>);
    if (read.kind === 'malformed') return { ok: false, error: `malformed positions payload (${read.reason})` };
    if (read.kind === 'position') positions.push(read.position);
  }
  return { ok: true, positions,
    ...(!Array.isArray(data) && data && typeof data === 'object' && 'historicalIdentities' in data
      ? { historicalIdentities: (data as Record<string, unknown>).historicalIdentities } : {}) };
}

async function readPositionsSnapshotResponse(res: Response): Promise<PositionsSnapshotRead> {
  if (!res.ok) return { ok: false, error: `HTTP ${res.status}` };
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return { ok: false, error: 'malformed positions payload' };
  }
  return readPositionsSnapshot(body);
}

/** The holdings scope a retained positions list is evidence for: the auth
 *  user plus the key mode. Set on every readable bootstrap; a bootstrap whose
 *  read fails under a DIFFERENT scope must not carry the old list. Cleared on
 *  disconnect with the rest of the per-session guards. */
let _positionsScopeKey: string | null = null;
function currentOrdersViewerKey(): string {
  const user = useAuthStore.getState().user;
  return JSON.stringify([user?.id, user?.orgId, user?.role]);
}

/** Include reconnect generation: reusing a key mode does not certify that the
 * underlying credential/account stayed the same. Unknown identity grants none. */
export function currentOrdersScopeKey(keyMode = useAccountStore.getState().keyMode): string | null {
  const user = useAuthStore.getState().user;
  if (!user || keyMode == null) return null;
  return JSON.stringify([user.id, user.orgId ?? null, user.role, keyMode, _connectionGeneration, _closeAuthorityGeneration]);
}

function positionsScopeKeyFor(keyMode: 'org' | 'user'): string {
  return `${useAuthStore.getState().user?.id ?? ''}|${keyMode}`;
}

function positionsSyncAfterFailure(prev: PositionsSyncState, error: string): PositionsSyncState {
  return { ...prev, lastErrorMs: Date.now(), lastError: error, consecutiveFailures: prev.consecutiveFailures + 1 };
}

function positionsSyncAfterSuccess(prev: PositionsSyncState): PositionsSyncState {
  return { synced: true, lastSuccessMs: Date.now(), lastErrorMs: prev.lastErrorMs, lastError: null, consecutiveFailures: 0 };
}

function positionsEndpointFor(keyMode: 'org' | 'user' | null): string {
  return keyMode === 'org' ? '/api/trader/positions' : '/api/kalshi/positions';
}

// Org-scope fills come from the relay's persisted fills table (poller-resolved
// user_id), not the live Kalshi fetch — the latter filters by trades.kalshi_order_id
// which silently drops any fill whose order didn't get logged with a non-null
// kalshiOrderId, which has been masking recent fills entirely in org mode.
function fillsEndpointFor(keyMode: 'org' | 'user' | null): string {
  return keyMode === 'org' ? '/api/trader/fills' : '/api/kalshi/fills';
}

let _historicalReadSeq = 0;
let _historicalPositionSeq = 0;
function historicalReadScope(): string {
  const { status, user } = useAuthStore.getState();
  return JSON.stringify([getAuthSessionEpoch(), historicalIdentityEpoch(), status, user?.id, user?.orgId,
    user?.role, useAccountStore.getState().keyMode, _connectionGeneration]);
}

function admitHistoricalSibling(payload: unknown, tickers: readonly string[], scope: string, seq: number,
  kind: HistoricalReadKind = 'fills'): void {
  const user = useAuthStore.getState().user;
  const currentSeq = kind === 'positions' ? _historicalPositionSeq : _historicalReadSeq;
  if (seq !== currentSeq || scope !== historicalReadScope()
      || useAccountStore.getState().status !== 'connected' || useAccountStore.getState().keyMode !== 'org'
      || useAuthStore.getState().status !== 'authenticated' || !user
      || !payload || typeof payload !== 'object') return;
  hydrateHistoricalIdentities(user, (payload as Record<string, unknown>).historicalIdentities, tickers, kind);
}

// Exported alongside parsePositions so org-wide surfaces reuse the same raw-
// Kalshi order parsing (remaining_count_fp, *_price_dollars) rather than a
// divergent hand-rolled copy.
export function parseOrders(data: Record<string, unknown>): KalshiOrder[] {
  const raw = (data.orders ?? data.results ?? data.data ?? []) as Record<string, unknown>[];
  if (!Array.isArray(raw)) return [];
  return raw.map((o): KalshiOrder => {
    // Reconstruct the trader-native {side, action} pair for DISPLAY. V2 is single-
    // book: a buy-NO is sent as an `ask` (sell YES), so it reads back as
    // side:"yes"/action:"sell" with outcome_side:"no". `outcome_side` is the
    // canonical trader side; we flip `action` when the order was placed cross-
    // framed so the pair stays coherent (a NO reads as {no, buy}, never {no, sell}).
    // Verified against live Kalshi reads — see reconstructOrderTraderSide.
    const { side, action } = reconstructOrderTraderSide(o);
    const reconciliation = (() => {
      const value = o.g1_order_reconciliation;
      if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
      const record = value as Record<string, unknown>;
      const actions = record.actions;
      if (!actions || typeof actions !== 'object' || Array.isArray(actions)) return undefined;
      const actionRecord = actions as Record<string, unknown>;
      if (
        record.source !== 'exact_order_detail' ||
        record.lifecycle !== 'resting_list_omitted' ||
        actionRecord.cancel !== 'available' ||
        actionRecord.decrease !== 'unavailable'
      ) return undefined;
      return {
        source: 'exact_order_detail' as const,
        lifecycle: 'resting_list_omitted' as const,
        verifiedAt: pickStr(record.verified_at) ?? '',
        marketStatus: pickStr(record.market_status) ?? null,
        marketStatusCheckedAt: pickStr(record.market_status_checked_at) ?? null,
        marketInactive:
          typeof record.market_inactive === 'boolean' ? record.market_inactive : null,
        actions: {
          cancel: 'available' as const,
          decrease: 'unavailable' as const,
        },
      };
    })();
    return {
      orderId: pickStr(o.order_id, o.id) ?? '',
      ticker: pickStr(o.ticker, o.market_ticker) ?? '',
      side,
      action,
      type: (o.type as 'limit' | 'market') ?? 'limit',
      status: pickStr(o.status) ?? 'resting',
      // Kalshi names the original order size `initial_count_fp` (a fixed-
      // point string, e.g. "1.00") — a resting order has no `count`/`count_fp`
      // field, so the legacy fallbacks always missed and count fell to 0.
      count:
        pickWholeContracts(o.initial_count, o.initial_count_fp, o.count, o.count_fp, o.quantity) ?? 0,
      remainingCount: pickWholeContracts(o.remaining_count, o.remaining_count_fp, o.remaining_quantity, o.quantity) ?? 0,
      fillCount: pickWholeContracts(o.fill_count, o.fill_count_fp),
      decreaseCount: pickWholeContracts(o.decrease_count, o.decrease_count_fp),
      yesPrice: dollarsToCents(o.yes_price_dollars, o.yes_price) || undefined,
      noPrice: dollarsToCents(o.no_price_dollars, o.no_price) || undefined,
      // Kalshi accepts `expiration_ts` (unix seconds) on order placement
      // but the orders LIST response surfaces the same value under
      // `expiration_time` as an ISO string. Earlier parser only checked
      // `expiration_ts` / `expires_at` so GTS orders read as GTC in
      // the Positions / Open Orders table. Try all the field shapes
      // we've seen in the wild and convert each to unix seconds.
      expirationTs: (() => {
        const num = pickNum(o.expiration_ts);
        if (num != null) return num;
        const isoCandidate =
          (typeof o.expiration_time === 'string' && o.expiration_time !== '')
            ? o.expiration_time
            : (typeof o.expires_at === 'string' && o.expires_at !== '')
              ? o.expires_at
              : null;
        if (isoCandidate) {
          const ms = new Date(isoCandidate).getTime();
          if (Number.isFinite(ms) && ms > 0) return Math.floor(ms / 1000);
        }
        return null;
      })(),
      createdTime: pickStr(o.created_time, o.created_at) ?? '',
      // Only an explicit boolean is evidence; absent stays unknown so an edit
      // never mistakes "not echoed" for "not reduce-only" (or the reverse).
      ...(typeof o.reduce_only === 'boolean' ? { reduceOnly: o.reduce_only } : {}),
      // Oscar blocker tagging rides on the venue echoing client_order_id on
      // reads (validated in the node1 smoke; relay-side enrichment from the
      // trades audit is the named fallback if it ever stops).
      clientOrderId: pickStr(o.client_order_id) || undefined,
      reconciliation,
    };
  });
}

/** Canonical relay orders envelope. Unlike parseOrders' compatibility parser,
 *  this validates transport shape before state can be replaced. A malformed
 *  successful REST/push body is a sync failure, never evidence of a flat book. */
export function parseOrdersSnapshot(data: unknown): KalshiOrder[] | null {
  if (!data || typeof data !== 'object') return null;
  const orders = (data as Record<string, unknown>).orders;
  if (!Array.isArray(orders)) return null;
  if (orders.some((order) => !order || typeof order !== 'object' || Array.isArray(order))) {
    return null;
  }
  const parsed = parseOrders({ orders });
  // #241: an ID-less row can't prove a target ID disappeared, so a non-empty
  // book containing one is not removal authority — treat the whole snapshot as
  // unreadable rather than let it retire a cancel fence. `{ orders: [] }` is
  // still a valid authoritative empty book (the #230/#238 canonical rule).
  if (parsed.some((order) => order.orderId === '')) return null;
  return parsed;
}

function logOpenOrdersEmptyTransition(
  source: 'rest' | 'push' | 'disconnect',
  previousCount: number,
): void {
  if (previousCount <= 0) return;
  const action = source === 'disconnect' ? 'cleared' : 'accepted non-empty→empty';
  console.warn(
    `[accountStore] ${new Date().toISOString()} source=${source} ${action} open-orders transition (previousOrders=${previousCount})`,
  );
}

export function parseFills(data: Record<string, unknown>, priceOrigin: 'venue' | 'ledger' = 'venue'): KalshiFill[] {
  const raw = (data.fills ?? data.results ?? data.data ?? []) as Record<string, unknown>[];
  if (!Array.isArray(raw)) return [];
  return raw.map((f): KalshiFill => {
    // Legacy `side` first (it pairs with `action` in the legacy convention the
    // P&L/position math uses). `outcome_side` flips on a sell fill, so preferring
    // it would mis-sign sell P&L and pick the wrong `${side}_price`. Fall back to
    // `outcome_side` only when legacy `side` is absent. Mirrors parseOrders.
    const side = ((f.side ?? f.outcome_side) as 'yes' | 'no') ?? 'yes';
    const price = dollarsToCents(f[`${side}_price_dollars`] ?? f.price_dollars, f[`${side}_price`] ?? f.price);
    return {
      fillId: pickStr(f.fill_id, f.trade_id, f.id) ?? `fill-${Date.now()}`,
      orderId: pickStr(f.order_id) ?? '',
      ticker: pickStr(f.ticker, f.market_ticker) ?? '',
      side,
      action: (f.action as 'buy' | 'sell') ?? 'buy',
      count: pickNum(f.count, f.count_fp, f.fill_count_fp, f.quantity) ?? 0,
      price,
      // The org endpoint is a persisted ledger: old rows may contain the
      // submitted limit as a fallback. Only its explicit venue tag proves an
      // execution price. Personal-key reads come directly from the venue.
      priceSource: priceOrigin === 'venue' ? 'venue'
        : f.g1_price_source === 'venue' || f.g1_price_source === 'audit' ? f.g1_price_source : null,
      createdAt: pickStr(f.created_at, f.created_time) ?? new Date().toISOString(),
      ...(f.bot === 'penelope' || f.bot === 'harvester' || f.bot === 'minigame' ? { bot: f.bot } : {}),
    };
  });
}

/** A fill row parseFills could only "parse" by inventing identity, price, size
 *  or time is not a fill — it is evidence the read is unreadable. Field names
 *  mirror parseFills' coalescing exactly so every shape it can faithfully parse
 *  is accepted, and nothing else. order_id stays optional (org-lane rows may
 *  predate kalshiOrderId capture). No Kalshi fill executes at 0¢, so a derived
 *  0 price always means the price fields were absent. */
function isUsableKalshiFillRow(f: Record<string, unknown>): boolean {
  const side = f.side ?? f.outcome_side;
  if (side !== 'yes' && side !== 'no') return false;
  if (f.action !== 'buy' && f.action !== 'sell') return false;
  if (pickStr(f.fill_id, f.trade_id, f.id) === undefined) return false;
  if (pickStr(f.ticker, f.market_ticker) === undefined) return false;
  const count = pickNum(f.count, f.count_fp, f.fill_count_fp, f.quantity);
  if (count === undefined || count <= 0) return false;
  const price = dollarsToCents(f[`${side}_price_dollars`] ?? f.price_dollars, f[`${side}_price`] ?? f.price);
  if (price <= 0) return false;
  const createdAt = pickStr(f.created_at, f.created_time);
  return createdAt !== undefined && Number.isFinite(Date.parse(createdAt));
}

/** Diagnostic only (#1025): names the first check `isUsableKalshiFillRow` fails,
 *  same checks in the same order, or null for a usable row. Runs only after a
 *  rejection, to say which row and why; it never gates anything. A separate
 *  copy on purpose (the #499 predicate above is untouched); an agreement test
 *  pins the two together. Never returns or logs a price. */
export function fillRowDefect(f: Record<string, unknown>): string | null {
  const side = f.side ?? f.outcome_side;
  if (side !== 'yes' && side !== 'no') return 'side';
  if (f.action !== 'buy' && f.action !== 'sell') return 'action';
  if (pickStr(f.fill_id, f.trade_id, f.id) === undefined) return 'fill_id';
  if (pickStr(f.ticker, f.market_ticker) === undefined) return 'ticker';
  const count = pickNum(f.count, f.count_fp, f.fill_count_fp, f.quantity);
  if (count === undefined || count <= 0) return 'count';
  const price = dollarsToCents(f[`${side}_price_dollars`] ?? f.price_dollars, f[`${side}_price`] ?? f.price);
  if (price <= 0) return 'price';
  const createdAt = pickStr(f.created_at, f.created_time);
  return createdAt !== undefined && Number.isFinite(Date.parse(createdAt)) ? null : 'time';
}

/** Why `parseFillsSnapshot` returned null, for the log and `fillsSync.lastError`. */
function fillsRejectionReason(data: unknown): string {
  if (!data || typeof data !== 'object') return 'rejected payload shape';
  const d = data as Record<string, unknown>;
  const raw = d.fills ?? d.results ?? d.data;
  if (!Array.isArray(raw)) return 'rejected payload shape';
  for (let i = 0; i < raw.length; i++) {
    const f = raw[i];
    if (!f || typeof f !== 'object' || Array.isArray(f)) return `rejected row ${i + 1}/${raw.length} check=shape`;
    const row = f as Record<string, unknown>;
    const check = fillRowDefect(row);
    if (check !== null) {
      return `rejected row ${i + 1}/${raw.length} check=${check}`;
    }
  }
  return 'rejected payload shape';
}

/** Fail-closed fills read (#499). parseFills defaults every missing field, so a
 *  malformed row inside a valid array used to become a synthetic displayable
 *  fill — manufactured Recent Fills history. Mirrors parseOrdersSnapshot
 *  (#238/#241) and the Polymarket fills decoder: whole-payload rejection, not
 *  per-row filtering, because a partially dropped tape is indistinguishable
 *  from a complete one. Returns null for "could not read the fills", which is
 *  never "the trader has no fills"; `{ fills: [] }` remains the only
 *  authoritative empty read. */
export function parseFillsSnapshot(data: unknown, priceOrigin: 'venue' | 'ledger' = 'venue'): KalshiFill[] | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  const raw = d.fills ?? d.results ?? d.data;
  if (!Array.isArray(raw)) return null;
  for (const f of raw) {
    if (!f || typeof f !== 'object' || Array.isArray(f)) return null;
    if (!isUsableKalshiFillRow(f as Record<string, unknown>)) return null;
  }
  return parseFills({ fills: raw }, priceOrigin);
}

type OrdersSnapshotRead =
  | { ok: true; orders: KalshiOrder[]; asOf: number | null }
  | { ok: false; error: string };

/** Read an open-orders response under ONE transport contract: a non-2xx, a body
 *  that isn't JSON, and a malformed successful envelope all mean "could not read
 *  the book", never "the book is flat". Returns a result rather than throwing so
 *  an orders-only fault degrades the orders surface instead of failing connect.
 *
 *  #238 introduced this for the bootstrap; #241 routes refreshOrders through it
 *  too, because the poll path's permissive parser mapped a malformed 200 onto []
 *  — which would have silently retired every pending cancel fence. */
async function readOrdersSnapshotResponse(res: Response): Promise<OrdersSnapshotRead> {
  if (!res.ok) return { ok: false, error: `HTTP ${res.status}` };
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return { ok: false, error: 'malformed orders payload' };
  }
  const orders = parseOrdersSnapshot(body);
  if (orders === null) return { ok: false, error: 'malformed orders payload' };
  return { ok: true, orders, asOf: readOrdersAsOf(body) };
}

interface KeyModeResponse {
  canTrade?: boolean;
  mode?: 'org' | 'user';
  useOrgKeys?: boolean;
  orgKeysAvailable?: boolean;
}

function keyModeInfoOf(data: KeyModeResponse): AccountState['keyModeInfo'] {
  const mode = data.mode === 'org' || data.mode === 'user' ? data.mode : null;
  return { mode, useOrgKeys: data.useOrgKeys === true, orgKeysAvailable: data.orgKeysAvailable === true };
}

/** One failed or rejected fills read: counted in `fillsSync` (every failure) and logged
 *  with its source and reason. The reason carries no price and no identifier. Logging is
 *  bounded per streak: only the 1st, 2nd, 4th, 8th... consecutive failure is written. */
function recordFillsReadFailure(source: 'rest' | 'push', reason: string): void {
  const prev = useAccountStore.getState().fillsSync;
  const failures = prev.consecutiveFailures + 1;
  if ((failures & (failures - 1)) === 0) {
    console.warn(`[accountStore] ${new Date().toISOString()} source=${source} fills read failed (${failures}x): ${reason}`);
  }
  useAccountStore.setState({
    fillsSync: { ...prev, lastErrorMs: Date.now(), lastError: reason, consecutiveFailures: failures },
  });
}

export const useAccountStore = create<AccountState>((set, get) => ({
  status: 'disconnected',
  error: null,
  ordersSync: { lastSuccessMs: null, lastErrorMs: null, lastError: null, consecutiveFailures: 0 },
  fillsSync: { lastSuccessMs: null, lastErrorMs: null, lastError: null, consecutiveFailures: 0 },
  ordersScopeKey: null,
  positionsSync: UNSYNCED_POSITIONS,
  keyHealth: [],
  credentialCount: 0,
  credentialsStatus: 'unknown',
  connectFailure: null,
  connectWanted: false,
  keyMode: null,
  keyModeInfo: null,
  balance: null,
  balanceHidden: false,
  positions: [],
  openOrders: [],
  kalshiCancelStates: {},
  recentFills: [],
  activityTicker: [],
  fillsSynced: false,
  realizedPnlCents: 0,
  realizedFeesCents: null,
  realizedPnlNetCents: null,
  openNotionalCents: 0,

  resetCredentialCheck: () => set({ credentialsStatus: 'unknown', credentialCount: 0, keyModeInfo: null, connectFailure: null }),

  checkCredentials: async () => {
    // Settings can replace/disable a key without changing mode or reconnecting.
    // Invalidate close authority before that asynchronous check; no credential
    // material is copied into browser state.
    const authorityGeneration = ++_closeAuthorityGeneration;
    const requestScope = currentOrdersScopeKey();
    const requestViewer = currentOrdersViewerKey();
    set({ ordersScopeKey: null });
    invalidateRelayPushSession();
    try {
      const res = await apiFetch('/api/trader/key-mode');
      if (authorityGeneration !== _closeAuthorityGeneration || requestScope !== currentOrdersScopeKey()
        || requestViewer !== currentOrdersViewerKey()) return;
      if (!res.ok) { set({ credentialCount: 0, credentialsStatus: 'error' }); return; }
      const data = await res.json() as KeyModeResponse;
      if (authorityGeneration !== _closeAuthorityGeneration || requestScope !== currentOrdersScopeKey()
        || requestViewer !== currentOrdersViewerKey()) return;
      const canTrade = data.canTrade === true;
      const keyMode: 'org' | 'user' | null = data.mode === 'org' || data.mode === 'user' ? data.mode : null;
      set({ credentialCount: canTrade ? 1 : 0, credentialsStatus: 'checked', keyMode,
        keyModeInfo: keyModeInfoOf(data),
        ordersScopeKey: get().keyMode === keyMode ? get().ordersScopeKey : null });
      const s = get().status;
      if (canTrade && (s === 'disconnected' || s === 'error')) {
        get().connect();
      } else if (canTrade && s === 'connected') {
        // One request per explicit credential check; no additional polling.
        await get().refreshOrders();
      }
    } catch {
      if (authorityGeneration !== _closeAuthorityGeneration || requestScope !== currentOrdersScopeKey()
        || requestViewer !== currentOrdersViewerKey()) return;
      set({ credentialCount: 0, credentialsStatus: 'error' });
    }
  },

  connect: async () => {
    // We want a live connection from here until disconnect() — arm the
    // self-heal watchdog (idempotent) so a stale-generation strand can't leave
    // the session wedged at 'connecting' forever. Set before the in-flight
    // guard so even a swallowed concurrent call registers the desire.
    if (!_desiredConnected) {
      _fillSoundSessionStartedAtMs = Date.now();
      _fillSoundSessionStartedMonoMs = performance.now();
    }
    _desiredConnected = true;
    if (!get().connectWanted) set({ connectWanted: true });
    ensureHealWatchdog(get);
    // Concurrency guard MUST be an in-flight flag, not a status check: the
    // retry ladder sets status 'connecting' before scheduling its timer, so
    // a status-based guard made every scheduled retry early-return — after
    // ONE transient failure the store wedged at 'connecting' forever
    // ("retrying in 3s (1/5)" that never retried), polling stopped, and the
    // session sat silently dead until a manual reload. That wedge is the
    // probable mechanism behind the recurring morning "orders invisible"
    // incidents (review finding, Jun 11).
    if (_connectInFlight) return;
    _connectInFlight = true;
    const generation = nextConnectionGeneration();
    invalidateRelayPushSession();
    const connectionUser = useAuthStore.getState().user;
    const sameConnectionUser = () => {
      const current = useAuthStore.getState().user;
      return connectionUser?.id === current?.id && connectionUser?.orgId === current?.orgId
        && connectionUser?.role === current?.role;
    };
    if (_connectRetryTimer) { clearTimeout(_connectRetryTimer); _connectRetryTimer = null; }
    set({ status: 'connecting', error: null });

    try {
      const modeRes = await apiFetch('/api/trader/key-mode');
      if (!modeRes.ok) {
        const errData = await modeRes.json().catch(() => ({}));
        throw new Error((errData as Record<string, string>).error ?? 'Failed to load key mode');
      }
      const modeData = await modeRes.json() as KeyModeResponse;
      if (!isCurrentConnectionGeneration(generation) || !sameConnectionUser()) return;
      const canTrade = modeData.canTrade === true;
      const keyMode: 'org' | 'user' = modeData.mode === 'org' ? 'org' : 'user';
      // A retained positions list is only evidence for the SAME holdings
      // scope (auth user + key mode). A scope change without an intervening
      // disconnect means the holdings moved under us, so last-good must not
      // carry across a failed read.
      const positionsScopeKey = positionsScopeKeyFor(keyMode);
      const positionsScopeChanged = _positionsScopeKey !== null && _positionsScopeKey !== positionsScopeKey;
      set({ credentialCount: canTrade ? 1 : 0, credentialsStatus: 'checked', keyMode,
        keyModeInfo: keyModeInfoOf(modeData),
        ordersScopeKey: get().keyMode === keyMode ? get().ordersScopeKey : null });

      if (!canTrade) {
        if (!isCurrentConnectionGeneration(generation)) return;
        _connectRetryAttempts = 0;
        set({ status: 'disconnected', connectFailure: null,
          error: 'No credentials available. Ask your manager to add org keys, or add personal keys in Settings.' });
        return;
      }

      // Balance probe. Two very different failure semantics by key mode:
      //  - PERSONAL: this is the auth check for a key the user just typed —
      //    a failure must fail the connect loudly (April-era behavior kept).
      //  - ORG: creds are managed server-side and already validated; a
      //    failure here means KALSHI is down (e.g. the Thursday 3–5am ET
      //    maintenance pause — docs.kalshi.com "Maintenance and Pauses"),
      //    and a venue outage must DEGRADE the Kalshi surfaces, not block
      //    the whole dash: Polymarket doesn't need Kalshi at all. Before
      //    the Jul-9 balance unmask, org traders never even hit Kalshi on
      //    this path (placeholder), so fatal-on-org would be a NEW
      //    outage coupling; managers/admins gain the resilience they never
      //    had. Balance stays null in the degraded state (UI shows '—').
      const balRes = await apiFetch('/api/kalshi/balance').catch(() => null);
      if (!isCurrentConnectionGeneration(generation)) return;
      let balance: number | null = null;
      let balanceHidden = false;
      if (balRes?.ok) {
        const balData = await balRes.json() as Record<string, unknown>;
        if (!isCurrentConnectionGeneration(generation)) return;
        // Org-keys mode returns the REAL org-account balance (relay unmasked
        // Jul-9 — balance is account-level, no per-trader slice exists) unless
        // a manager hid it (#1205: a refusal, handled below). Null only when
        // the fields are genuinely absent (e.g. an
        // old relay still serving the placeholder) so the UI hides the
        // value instead of showing "$0.00".
        balance = readKalshiReportedBalanceCents(balData);
      } else {
        const errData = balRes ? await balRes.json().catch(() => ({})) : {};
        if (!isCurrentConnectionGeneration(generation)) return;
        // #1205: a hidden balance is a permission state, never a failed
        // connect in either key mode.
        if (isOrgBalanceHiddenBody(errData)) balanceHidden = true;
        else if (keyMode !== 'org') {
          throw new Error((errData as Record<string, string>).error ?? 'Failed to fetch balance');
        }
      }

      if (!sameConnectionUser()) return;
      const ordersScopeKey = currentOrdersScopeKey(keyMode);
      const identityScope = historicalReadScope(), identitySeq = ++_historicalPositionSeq;
      const [posRes, ordRes, pnlRes] = await Promise.all([
        apiFetch(positionsEndpointFor(keyMode)),
        apiFetch('/api/kalshi/orders'),
        keyMode === 'org' ? apiFetch('/api/trader/pnl') : Promise.resolve(null),
      ]);
      if (!isCurrentConnectionGeneration(generation) || !sameConnectionUser()
        || ordersScopeKey !== currentOrdersScopeKey()) return;

      // Positions read under the same fail-closed contract as orders (#881
      // review F4): an HTTP failure or malformed 200 keeps the last-good list
      // and records a positionsSync failure; it never becomes an empty
      // account. Only a readable snapshot (including a valid empty one) may
      // mark the account synced.
      const positionsRead = await readPositionsSnapshotResponse(posRes);
      // Orders fail CLOSED here (#238 / Danielle DNL-001 on #230). This path
      // runs on every RECONNECT, and mapping a 502 or a malformed 200 onto []
      // replaced a live resting book with an empty list while stamping a fresh
      // ordersSync success — the trader saw "No open orders" beside a healthy
      // sync clock (the #230 disappearing-orders class). refreshOrders already
      // keeps last-good when the transport fails; bootstrap did not, and now
      // reads under the same contract.
      const ordersRead = await readOrdersSnapshotResponse(ordRes);
      const pnl = pnlRes && pnlRes.ok
        ? await pnlRes.json() as {
            realizedPnlCents?: number;
            realizedFeesCents?: number | null;
            realizedPnlNetCents?: number | null;
            openNotionalCents?: number;
          }
        : null;
      if (!isCurrentConnectionGeneration(generation) || !sameConnectionUser()
        || ordersScopeKey !== currentOrdersScopeKey()) return;

      _connectRetryAttempts = 0;
      const prevSync = get().ordersSync;
      if (!ordersRead.ok) {
        console.warn(
          `[accountStore] open-orders bootstrap failed (${prevSync.consecutiveFailures + 1}x): ${ordersRead.error}`,
        );
      }
      const prevPositionsSync = get().positionsSync;
      if (!positionsRead.ok) {
        console.warn(
          `[accountStore] positions bootstrap failed (${prevPositionsSync.consecutiveFailures + 1}x): ${positionsRead.error}`,
        );
      }
      if (positionsRead.ok || positionsScopeChanged) _positionsScopeKey = positionsScopeKey;
      // #968: a push frame may already have applied a newer view of the book.
      const ordersAdmitted = ordersRead.ok && !_ordersVintage.superseded(ordersScopeKey, ordersRead.asOf)
        ? admitOrdersSnapshot(get().kalshiCancelStates, ordersRead.orders, ordersRead.asOf)
        : null;
      set({
        status: 'connected', error: null, connectFailure: null, balance, balanceHidden,
        // positions is written ONLY on a readable snapshot. On a failed read
        // the retained list stays by identity (transient reconnect keeps the
        // holdings evidence); when the holdings scope changed, the retained
        // list is not evidence for this account and is cleared unsynced.
        ...(positionsRead.ok
          ? { positions: positionsRead.positions, positionsSync: positionsSyncAfterSuccess(prevPositionsSync) }
          : positionsScopeChanged
            ? { positions: [], positionsSync: positionsSyncAfterFailure(UNSYNCED_POSITIONS, positionsRead.error) }
            : { positionsSync: positionsSyncAfterFailure(prevPositionsSync, positionsRead.error) }),
        // openOrders is written ONLY on a snapshot we could actually read.
        // Omitting the key (rather than writing the old value back) preserves
        // the retained list by identity, so nothing downstream re-renders on a
        // failed read.
        ...(ordersAdmitted
          ? {
              openOrders: reconcilePlacementConfirmations(ordersAdmitted.orders),
              ordersScopeKey,
              kalshiCancelStates: ordersAdmitted.kalshiCancelStates,
            }
          : null),
        // On a good read the bootstrap has a fresh snapshot — stamp the sync
        // clock so the staleness badge doesn't flash a pre-reconnect age. On a
        // failed read, keep the prior lastSuccessMs: a false success would hide
        // the staleness badge over an unreadable book.
        ordersSync: ordersRead.ok
          ? { lastSuccessMs: Date.now(), lastErrorMs: null, lastError: null, consecutiveFailures: 0 }
          : {
              ...prevSync,
              lastErrorMs: Date.now(),
              lastError: ordersRead.error,
              consecutiveFailures: prevSync.consecutiveFailures + 1,
            },
        realizedPnlCents: pnl?.realizedPnlCents ?? 0,
        realizedFeesCents: pnl?.realizedFeesCents ?? null,
        realizedPnlNetCents: pnl?.realizedPnlNetCents ?? null,
        openNotionalCents: pnl?.openNotionalCents ?? 0,
      });

      if (positionsRead.ok) admitHistoricalSibling(positionsRead, positionsRead.positions.map(p => p.ticker),
        identityScope, identitySeq, 'positions');
      startPolling(() => {
        const s = get().status;
        if (!isCurrentConnectionGeneration(generation) || s !== 'connected') {
          stopPolling();
          return;
        }

        readPolledBalance(generation);

        get().refreshPositions().catch(() => {});
        get().refreshOrders().catch(() => {});
        get().refreshPnl().catch(() => {});
      }, () => {
        if (!isCurrentConnectionGeneration(generation) || get().status !== 'connected') return;
        // Owed read with push healthy: back off (D11). With the topic down, the #843 cadence is untouched.
        retireStaleFillsWork();
        if (fillsReadHeldBack(topicActive('fills'))) return;
        get().refreshFills().catch(() => {});
      });
    } catch (err) {
      if (!isCurrentConnectionGeneration(generation)) return;
      const message = err instanceof Error ? err.message : 'Connection failed';
      stopPolling();
      if (_connectRetryAttempts < MAX_CONNECT_RETRIES) {
        const delay = BASE_CONNECT_RETRY_MS * 2 ** _connectRetryAttempts;
        _connectRetryAttempts++;
        set({ status: 'connecting', connectFailure: message,
          error: `${message} — retrying in ${Math.round(delay / 1000)}s (${_connectRetryAttempts}/${MAX_CONNECT_RETRIES})` });
        _connectRetryTimer = setTimeout(() => {
          if (!isCurrentConnectionGeneration(generation)) return;
          void get().connect();
        }, delay);
      } else {
        _connectRetryAttempts = 0;
        set({ status: 'error', connectFailure: message, error: `${message} — auto-retrying every 60s` });
        // Terminal 'error' used to be PERMANENT until a manual reload —
        // morning transients left traders with a dead, silently-frozen
        // session (orders list empty, no recovery). Keep nudging gently.
        _connectRetryTimer = setTimeout(() => {
          if (!isCurrentConnectionGeneration(generation)) return;
          if (get().status !== 'error') return;
          void get().connect();
        }, SLOW_CONNECT_RETRY_MS);
      }
    } finally {
      _connectInFlight = false;
    }
  },

  disconnect: () => {
    // Connection is no longer wanted — silence the self-heal watchdog so it
    // doesn't fight a deliberate disconnect/logout, and tear down its interval
    // so a logged-out tab leaves no lingering timer. connect() re-arms it (it
    // calls ensureHealWatchdog before the in-flight guard), so a swallowed
    // remount-reconnect still gets watchdog coverage.
    _desiredConnected = false;
    _fillSoundSessionStartedAtMs = null;
    _fillSoundSessionStartedMonoMs = null;
    _fillSoundSessionGeneration += 1;
    if (_healWatchdog) { clearInterval(_healWatchdog); _healWatchdog = null; }
    _connectionGeneration += 1;
    _historicalReadSeq += 1;
    _historicalPositionSeq += 1;
    invalidateHistoricalIdentitySession();
    stopPolling();
    if (_connectRetryTimer) { clearTimeout(_connectRetryTimer); _connectRetryTimer = null; }
    _connectRetryAttempts = 0;
    _positionsScopeKey = null;
    _seenFillIds.clear();
    _seenFillOrder.length = 0;
    _seenOrderIds.clear();
    logOpenOrdersEmptyTransition('disconnect', get().openOrders.length);
    // #241: drop every cancel fence and its timer. A logged-out tab must not
    // keep a timeout armed, and a reconnected session starts unfenced.
    forgetAllCancelAttempts();
    forgetAllPlacementConfirmations();
    // Deploy C timers: a deferred orders fallback / positions debounce must not
    // fire a REST read into a logged-out or reconnecting session.
    clearOrdersKick();
    clearPositionsFillDebounce();
    _lastOrdersFrameAt = 0;
    _lastOrdersKickAt = 0;
    set({
      status: 'disconnected',
      error: null,
      connectFailure: null,
      connectWanted: false,
      kalshiCancelStates: {},
      ordersSync: { lastSuccessMs: null, lastErrorMs: null, lastError: null, consecutiveFailures: 0 },
      fillsSync: { lastSuccessMs: null, lastErrorMs: null, lastError: null, consecutiveFailures: 0 },
      ordersScopeKey: null,
      positionsSync: UNSYNCED_POSITIONS,
      keyHealth: [],
      keyMode: null,
      balance: null,
      balanceHidden: false,
      positions: [],
      openOrders: [],
      recentFills: [],
      activityTicker: [],
      fillsSynced: false,
      realizedPnlCents: 0,
      realizedFeesCents: null,
      realizedPnlNetCents: null,
      openNotionalCents: 0,
    });
  },

  refreshPositions: async () => {
    if (get().status !== 'connected') return false;
    const generation = _connectionGeneration;
    const previous = _positionsRefreshInFlight?.generation === generation
      ? _positionsRefreshInFlight : null;
    // Synchronous fill batches share a read started in the next microtask.
    // A caller arriving AFTER dispatch must await a subsequent snapshot: an
    // in-flight response need not include its fill or close-position action.
    if (previous && !previous.started) return previous.promise;
    const flight = { generation, started: false, promise: Promise.resolve(false) };
    flight.promise = (previous ? previous.promise.catch(() => false) : Promise.resolve()).then(async () => {
      if (!isCurrentConnectionGeneration(generation) || get().status !== 'connected') return false;
      flight.started = true;
      return get().readPositionsOnce();
    }).finally(() => {
      // A retired account's completion must not clear the replacement flight.
      if (_positionsRefreshInFlight === flight) _positionsRefreshInFlight = null;
    });
    _positionsRefreshInFlight = flight;
    return flight.promise;
  },
  readPositionsOnce: async () => {
    const generation = _connectionGeneration;
    const scope = historicalReadScope(), identitySeq = ++_historicalPositionSeq;
    if (get().status !== 'connected') return false;
    const recordFailure = (reason: string) => {
      if (!isCurrentConnectionGeneration(generation)) return;
      const prev = get().positionsSync;
      // Keep the last-good list (a failed read is not a flat account) but make
      // the failure VISIBLE to the P&L coverage surfaces and diagnostics.
      console.warn(
        `[accountStore] ${new Date().toISOString()} source=rest positions refresh failed (${prev.consecutiveFailures + 1}x): ${reason}`,
      );
      set({ positionsSync: positionsSyncAfterFailure(prev, reason) });
    };
    const controller = new AbortController();
    let timeout: ReturnType<typeof setTimeout> | undefined;
    try {
      // Bound both headers and body. One hung read must not pin all later
      // refreshes; a late response is parsed but never published after expiry.
      const deadline = new Promise<never>((_, reject) => {
        timeout = setTimeout(() => {
          reject(new Error('positions refresh timed out'));
          controller.abort();
        }, 10_000);
      });
      const read = await Promise.race([
        // `priority: 'low'` (speed-demon): a background refresh never competes
        // with an order request for the connection.
        apiFetch(positionsEndpointFor(get().keyMode), { signal: controller.signal, priority: 'low' })
          .then(readPositionsSnapshotResponse),
        deadline,
      ]);
      if (!read.ok) {
        recordFailure(read.error);
        return false;
      }
      if (!isCurrentConnectionGeneration(generation) || scope !== historicalReadScope()
          || get().status !== 'connected') return false;
      set({ positions: read.positions, positionsSync: positionsSyncAfterSuccess(get().positionsSync) });
      admitHistoricalSibling(read, read.positions.map(p => p.ticker), scope, identitySeq, 'positions');
      return true;
    } catch (err) {
      recordFailure(err instanceof Error ? err.message : 'network error');
      return false;
    } finally {
      if (timeout !== undefined) clearTimeout(timeout);
    }
  },

  refreshPnl: async () => {
    const generation = _connectionGeneration;
    if (get().status !== 'connected' || get().keyMode !== 'org') return;
    try {
      const res = await apiFetch('/api/trader/pnl', { priority: 'low' });
      if (!res.ok) return;
      const pnl = await res.json() as {
        realizedPnlCents?: number;
        realizedFeesCents?: number | null;
        realizedPnlNetCents?: number | null;
        openNotionalCents?: number;
      };
      if (!isCurrentConnectionGeneration(generation) || get().status !== 'connected') return;
      set({
        realizedPnlCents: pnl.realizedPnlCents ?? 0,
        realizedFeesCents: pnl.realizedFeesCents ?? null,
        realizedPnlNetCents: pnl.realizedPnlNetCents ?? null,
        openNotionalCents: pnl.openNotionalCents ?? 0,
      });
    } catch {}
  },

  refreshFills: async () => {
    const generation = _connectionGeneration;
    const scope = historicalReadScope(), seq = ++_historicalReadSeq;
    if (get().status !== 'connected') return;
    retireStaleFillsWork();
    const sentMono = performance.now(), seqAtSend = _fillsFailureSeq, epochAtSend = _fillsReadEpoch;
    _fillsReadsInFlight += 1; _fillsLastSendMono = sentMono; _fillsWorkScope = scope;
    // #1025: every failure runs the same generation/scope/connected fence the
    // success path does, so a stale connection can't arm the new one's poll.
    // Brendan 17694: a read whose epoch was retired (expired hang, scope change, reset)
    // must not touch the replacement's retry or health state, on success or failure.
    const retired = () => epochAtSend !== _fillsReadEpoch;
    const fail = (reason: string) => {
      if (retired() || !isCurrentConnectionGeneration(generation) || scope !== historicalReadScope()
          || get().status !== 'connected') return;
      _fillsRestFailures += 1;
      const wait = FILLS_REARM_BACKOFF_MS[Math.min(_fillsRestFailures, FILLS_REARM_BACKOFF_MS.length) - 1];
      _fillsFailureSeq += 1;
      _fillsWorkScope = scope;
      _fillsReadDueAtMono = sentMono + wait; // from send time, so latency doesn't slip the schedule
      recordFillsReadFailure('rest', reason);
      _fillsReadOwed = true;
      reconcilePolling(false);
    };
    let reason = 'network';
    try {
      const requestStarted = localClockStamp();
      const keyMode = get().keyMode;
      const res = await apiFetch(fillsEndpointFor(keyMode), { priority: 'low' });
      if (!res.ok) { fail(`http ${res.status}`); return; }
      reason = 'rejected payload shape';
      const clock = createRelayClock(Number(res.headers?.get('X-Relay-Time-Ms')), requestStarted);
      // Fail-closed (#499): a malformed body/row is "could not read the fills",
      // so it lands nothing and — critically — grants no empty-history
      // authority. Only a validated snapshot (including a validated []) latches
      // fillsSynced, the one signal ActivityTicker may claim emptiness from.
      const payload: unknown = await res.json();
      const fills = parseFillsSnapshot(payload, keyMode === 'org' ? 'ledger' : 'venue');
      if (fills === null) { fail(fillsRejectionReason(payload)); return; }
      reason = 'read processing error';
      if (retired() || !isCurrentConnectionGeneration(generation) || scope !== historicalReadScope()
          || get().status !== 'connected') return;
      get().ingestFillBatch(fills, clock);
      admitHistoricalSibling(payload, fills.map(f => f.ticker), scope, seq);
      const prev = get().fillsSync;
      set({
        fillsSynced: true,
        fillsSync: { lastSuccessMs: Date.now(), lastErrorMs: prev.lastErrorMs, lastError: prev.lastError, consecutiveFailures: 0 },
      });
      // Only a validated REST read clears the owed read, and only if no failure
      // arrived after it was sent; polling stands down if push is healthy.
      if (_fillsFailureSeq === seqAtSend) {
        _fillsReadOwed = false; _fillsReadDueAtMono = 0; _fillsRestFailures = 0;
        reconcilePolling(false);
      }
    } catch { fail(reason); } finally { if (epochAtSend === _fillsReadEpoch && _fillsReadsInFlight > 0) _fillsReadsInFlight -= 1; }
  },

  refreshOrders: async () => {
    const generation = _connectionGeneration;
    const ordersScopeKey = currentOrdersScopeKey();
    if (get().status === 'disconnected') return;
    const recordFailure = (reason: string) => {
      if (!isCurrentConnectionGeneration(generation) || ordersScopeKey !== currentOrdersScopeKey()) return;
      const prev = get().ordersSync;
      const failures = prev.consecutiveFailures + 1;
      // Keep the stale list (better than blanking mid-session) but make the
      // failure VISIBLE: state for the UI badge, console for diagnostics.
      console.warn(
        `[accountStore] ${new Date().toISOString()} source=rest open-orders refresh failed (${failures}x): ${reason}`,
      );
      set({
        ordersSync: {
          ...prev,
          lastErrorMs: Date.now(),
          lastError: reason,
          consecutiveFailures: failures,
        },
      });
    };
    try {
      // speed-demon Deploy C: polls yield to order writes ('high') in the
      // browser's fetch scheduler.
      const res = await apiFetch('/api/kalshi/orders', { priority: 'low' });
      // Same fail-closed transport contract the bootstrap uses (#238), so a
      // malformed 200 records a sync failure and keeps the last-good list
      // instead of masquerading as a flat book (#241).
      const read = await readOrdersSnapshotResponse(res);
      if (!read.ok) {
        recordFailure(read.error);
        return;
      }
      if (!isCurrentConnectionGeneration(generation) || get().status === 'disconnected'
        || ordersScopeKey !== currentOrdersScopeKey()) return;
      // #968: a slow body behind a newer push frame is a read of an older
      // book. It must not roll the list back, and it is not fresh evidence
      // either: the newer frame already stamped the sync clock.
      if (_ordersVintage.superseded(ordersScopeKey, read.asOf)) return;
      const admitted = admitOrdersSnapshot(get().kalshiCancelStates, read.orders, read.asOf);
      const orders = reconcilePlacementConfirmations(admitted.orders);
      if (orders.length === 0) {
        logOpenOrdersEmptyTransition('rest', get().openOrders.length);
      }
      set({
        openOrders: orders,
        ordersScopeKey,
        kalshiCancelStates: admitted.kalshiCancelStates,
        ordersSync: {
          lastSuccessMs: Date.now(),
          lastErrorMs: get().ordersSync.lastErrorMs,
          lastError: null,
          consecutiveFailures: 0,
        },
      });
    } catch (err) {
      recordFailure(err instanceof Error ? err.message : 'network error');
    }
  },

  processFill: (fill: KalshiFill, clock?: RelayClock | null) => {
    if (_seenFillIds.has(fill.fillId)) return;
    recordSoundDecision('received');
    _seenFillIds.add(fill.fillId);
    _seenFillOrder.push(fill.fillId);
    while (_seenFillIds.size > MAX_SEEN_FILLS) {
      const oldest = _seenFillOrder.shift();
      if (oldest) _seenFillIds.delete(oldest);
    }

    // Normalized, not prepend-and-slice (#769). The old form let payload order
    // decide the retained set: a newest-first batch built the list backwards and,
    // past the cap, evicted the newest fills before any component saw them.
    // Sorting here keeps the invariant true for EVERY path into the store —
    // batch or single, REST or push — rather than only the one that remembers.
    const recentFills = retainNewestFills([fill, ...get().recentFills], MAX_RECENT_FILLS);
    const activityTicker = recentFills.slice(0, RECENT_FILL_LIMIT);
    const confirmation = _placementConfirmations.get(fill.orderId);
    if (confirmation) {
      const remainingCount = Math.max(0, confirmation.order.remainingCount - fill.count);
      if (remainingCount === 0) {
        forgetPlacementConfirmation(fill.orderId);
        set({
          recentFills,
          activityTicker,
          openOrders: get().openOrders.filter((order) => order.orderId !== fill.orderId),
        });
      } else {
        const confirmedOrder = { ...confirmation.order, remainingCount };
        _placementConfirmations.set(fill.orderId, {
          ...confirmation,
          order: confirmedOrder,
        });
        set({
          recentFills,
          activityTicker,
          openOrders: get().openOrders.map((order) =>
            order.orderId === fill.orderId ? confirmedOrder : order,
          ),
        });
      }
    } else {
      set({ recentFills, activityTicker });
    }

    const fillCreatedAtMs = new Date(fill.createdAt).getTime();
    const bounds = clock?.();
    // New relay evidence removes browser/venue wall-clock comparison. Keep
    // the old gate only for mixed-version relays/direct legacy callers that
    // provide no clock evidence; never substitute receipt time for fill age.
    const nowUpperMs = bounds?.upperMs ?? Date.now();
    const fillAgeMs = nowUpperMs - fillCreatedAtMs;
    const sessionStartMs = bounds && _fillSoundSessionStartedMonoMs !== null
      ? nowUpperMs - Math.max(0, performance.now() - _fillSoundSessionStartedMonoMs)
      : _fillSoundSessionStartedAtMs;
    const belongsToCurrentSoundSession = sessionStartMs === null || fillCreatedAtMs >= sessionStartMs;
    const isRecent = belongsToCurrentSoundSession
      && fillAgeMs >= -FILL_SOUND_FUTURE_SKEW_MS && fillAgeMs < FILL_SOUND_RECENCY_MS;
    // First-fill status is a fact about the ORDER, not about this browser
    // session, so it is recorded for every accepted fill — including the
    // history the REST bootstrap hydrates, which is silent (#795). Keeping the
    // bookkeeping inside the sound gate below meant an order whose earlier
    // fills predated this page load was unknown to the classifier: its next
    // ordinary fill was announced as a NEW ORDER, ringing the ka-ching and
    // taking #189's threshold bypass, which exists for genuinely new orders.
    // `sound-plan.md` and the alert registry both define this tone as "the
    // first fill of an order", never "the first one this tab happened to see".
    //
    // The 006b debrief (Apr 5) put the bookkeeping behind the gate for the
    // opposite failure — a historical flood consuming the ka-ching so live
    // fills only ever got the standard tone. That depended on there being no
    // fill-id dedup and no age gate; `_seenFillIds` and the recency window both
    // post-date it, so the same fill can no longer be counted twice and the
    // hazard the gate was defending against is closed by other means.
    //
    // Consumed even below the threshold (#189): a sub-threshold first fill IS
    // the ka-ching, so a later big fill must not replay it.
    const isFirstFill = !_seenOrderIds.has(fill.orderId);
    _seenOrderIds.add(fill.orderId);

    const snd = readSoundSettings();
    // Doom 10-17: a bot order's fill (the relay tags `bot`) rings only while the Penelope-fills channel is on. A gate on
    // the fill tones, never a tone of its own; counted as 'disabled' in the diagnostics.
    const botMuted = fill.bot !== undefined && !snd.botFillSoundEnabled;
    if (!belongsToCurrentSoundSession) recordSoundDecision('preSession');
    else if (!isRecent) recordSoundDecision('notRecent');
    else if (!snd.tradeSoundEnabled || botMuted) recordSoundDecision('disabled');
    else if (!(isFirstFill && snd.firstSaleSoundEnabled) && fill.count < snd.minFillContracts) recordSoundDecision('belowMinimum');
    if (isRecent && snd.tradeSoundEnabled && !botMuted) {
      const isFirstFillAlert = isFirstFill && snd.firstSaleSoundEnabled;
      if (isFirstFillAlert || fill.count >= snd.minFillContracts) {
        // Per-alert volume (#232): the channel whose tone this is decides its
        // own multiplier. Read INSIDE the closure, not here — a pending sound
        // can sit in the unlock queue across a settings change, and the volume
        // that matters is the one in force when it actually sounds.
        const channel: AlertChannelId =
          fill.count >= 1000 && snd.whaleSoundEnabled ? 'whaleFill'
          : isFirstFillAlert ? 'firstFill'
          : 'fill';
        const tone = channel === 'whaleFill' ? playWhaleTone
          : channel === 'firstFill' ? playKaChingTone
          : playFillTone;
        const playFn = () => tone(getChannelVolume(channel));

        const audioScope = () => {
          const auth = useAuthStore.getState();
          return auth.status === 'authenticated' && auth.user && get().keyMode
            ? JSON.stringify([auth.user.id, auth.user.orgId, auth.user.role, get().keyMode, 'kalshi']) : null;
        };
        const scope = audioScope();
        const eligible = () => {
          const settings = readSoundSettings();
          return settings.tradeSoundEnabled && ((isFirstFill && settings.firstSaleSoundEnabled)
            || fill.count >= settings.minFillContracts);
        };
        if (scope && typeof SharedWorker !== 'undefined') {
          watchFillAudioScope();
          const queuedAt = localClockStamp();
          const generation = _fillSoundSessionGeneration;
          const valid = () => generation === _fillSoundSessionGeneration && scope === audioScope()
            && elapsedSince(queuedAt) < FILL_SOUND_RECENCY_MS;
          const ready = () => getAudioContext()?.state === 'running';
          const deliver = () => {
            if (!valid() || !eligible()) return;
            if (!ready()) { recordSoundDecision('queued'); setPendingSound(deliver); return; }
            let blocked = false;
            const result = coordinateFillAudio({ key: JSON.stringify([scope, fill.fillId]), valid, ready,
              attempt: canStart => {
                const current = readSoundSettings();
                const selected = fill.count >= 1000 && current.whaleSoundEnabled ? 'whaleFill'
                  : isFirstFill && current.firstSaleSoundEnabled ? 'firstFill' : 'fill';
                const outcome = tryFillSoundNow(selected, getChannelVolume(selected), () => canStart() && eligible());
                blocked = outcome.kind === 'not-started' && outcome.reason === 'blocked';
                reportSynthAttemptHealth(outcome);
                return outcome;
              } });
            void result?.then(outcome => {
              if (outcome === 'suppressed') recordSoundDecision('coordSuppressed');
              if (outcome === 'expired') recordSoundDecision('coordExpired');
              if (outcome === 'unknown' || outcome === 'capacity') {
                reportFillCoordinationFailure(outcome);
              }
              if (!valid() || !eligible()) return;
              if (outcome === 'fallback') {
                const fallback = () => {
                  if (!valid() || !eligible()) return;
                  const current = readSoundSettings();
                  const selected = fill.count >= 1000 && current.whaleSoundEnabled ? 'whaleFill'
                    : isFirstFill && current.firstSaleSoundEnabled ? 'firstFill' : 'fill';
                  const currentTone = selected === 'whaleFill' ? playWhaleTone
                    : selected === 'firstFill' ? playKaChingTone : playFillTone;
                  currentTone(getChannelVolume(selected));
                };
                if (ready()) fallback(); else setPendingSound(fallback);
              } else if (blocked || (outcome === 'expired' && !ready())) setPendingSound(deliver);
            });
          };
          deliver();
        } else {
          const ctx = getAudioContext();
          if (ctx?.state === 'running') { playFn(); }
          else {
          // A tone that cannot play because the browser has not been clicked
          // yet goes into the unlock queue, and that queue drains whenever the
          // gesture finally arrives — which may be many minutes later. Judging
          // freshness only at ingest and then sounding the tone arbitrarily
          // later is how a trader hears a burst of chimes with nothing new on
          // screen (#795): the fills are real, but the alert is no longer about
          // anything that just happened. The queue exists to bridge the SHORT
          // locked window after a refresh (8ee06e0a), and #528 requires that a
          // reload never become a burst of alerts.
          //
          // The clock here is when the tone was QUEUED, not when the fill was
          // created. Measuring from creation would re-break the boundary #528
          // fixed: a fill legitimately discovered 29s after it executed has
          // already spent almost the whole window before it is queued, so it
          // would be dropped the moment audio unlocked. Time spent waiting for
          // the browser is the thing being bounded, and it is the only part of
          // the delay this queue is responsible for.
          //
          // Nothing is lost but the stale alert — the row is in Recent Fills
          // and in history either way.
          recordSoundDecision('queued');
          const queuedAt = localClockStamp();
          const soundGeneration = _fillSoundSessionGeneration;
          setPendingSound(() => {
            if (soundGeneration !== _fillSoundSessionGeneration || elapsedSince(queuedAt) >= FILL_SOUND_RECENCY_MS || !eligible()) return;
            playFn();
          });
          }
        }
      }
    }

    schedulePositionsRefreshForFill();
  },

  /**
   * The ONE entry point for a multi-fill payload (#769) — the REST bootstrap
   * and the relay push `fills` topic both come through here.
   *
   * Deliberately thin. The retention guarantee is not enforced at this boundary
   * but inside `processFill`, which normalizes the slice on every insertion:
   * that holds for a single live fill as well as a batch, so no future caller
   * can reintroduce the defect by not routing through here. What this function
   * buys is that the two callers cannot DRIFT — one named seam, one contract,
   * one place a test can target both paths.
   *
   * It deliberately does NOT reorder the batch. Feeding fills in payload order
   * keeps the freshness and first-fill sound rules exactly as they were, which
   * the issue requires; sorting would move an order's ka-ching from its newest
   * fill to its oldest. Ordering is a retention concern, and it is handled
   * where retention happens.
   */
  ingestFillBatch: (fills: readonly KalshiFill[], clock?: RelayClock | null) => {
    for (const fill of fills) get().processFill(fill, clock);
  },

  placeOrder: async (input: OrderInput) => {
    try {
      const order = await postOrderRaw(input);

      // #1229: a 201 whose remainder Kalshi's self-trade prevention cancelled
      // is not a placement. Nothing filled: a definite non-placement, rung as
      // a rejection (the descent below) and thrown so the control shows why.
      // Something filled: the fills are real and nothing rests; resolve with
      // the cancel attached so the caller renders the amber line, and ring
      // no order-placed tone (the fills ring their own).
      const selfTradeCancel = readSelfTradeCancel(order);
      if (selfTradeCancel != null && selfTradeCancel.filled === 0) {
        get().refreshOrders().catch(() => {});
        throw new SelfTradeCancelledError(selfTradeCancel);
      }

      const placed: PlacedKalshiOrder = {
        orderId: pickStr(order.order_id, order.id) ?? '',
        ticker: input.ticker,
        side: input.side,
        action: input.action,
        type: input.type,
        status: pickStr(order.status) ?? (selfTradeCancel ? 'canceled' : 'resting'),
        count: input.count,
        remainingCount: pickWholeContracts(order.remaining_count, order.remaining_count_fp) ?? input.count,
        yesPrice: input.side === 'yes' ? input.price : undefined,
        noPrice: input.side === 'no' ? input.price : undefined,
        createdTime: pickStr(order.created_time) ?? new Date().toISOString(),
        ...(selfTradeCancel ? { selfTradeCancel } : {}),
      };

      // Project only explicit, fully identifiable RESTING evidence. Missing
      // status/remaining fields, immediate fills, rejects and market orders do
      // not enter the fence and therefore cannot mint a phantom open order.
      const rawStatus = pickStr(order.status);
      const rawRemaining = pickWholeContracts(order.remaining_count, order.remaining_count_fp);
      if (placed.orderId && rawStatus === 'resting' && rawRemaining != null && rawRemaining > 0) {
        _placementConfirmations.set(placed.orderId, {
          order: placed,
          expiresAtMs: Date.now() + KALSHI_PLACEMENT_CONFIRMATION_TIMEOUT_MS,
        });
        set({
          openOrders: [
            ...get().openOrders.filter((candidate) => candidate.orderId !== placed.orderId),
            placed,
          ],
        });
        // Mirror the accepted resting order into the per-ticker open-order lane
        // (ladder, GameTable, Game Detail markers) so it paints now instead of on
        // the next relay frame. Market orders carry no side-native price and are
        // never mirrored; the same resting-evidence gate as the fence applies.
        if (input.price !== undefined) {
          emitPlacement({
            ticker: input.ticker,
            orderId: placed.orderId,
            side: input.side,
            action: input.action,
            priceCents: input.price,
            remainingCount: rawRemaining,
          });
        }
      }

      if (selfTradeCancel == null) {
        const snd = readSoundSettings();
        if (snd.orderPlacedSoundEnabled) playOrderPlacedTone(getChannelVolume('orderPlaced'));
      }

      kickOrdersRefresh('place');

      return placed;
    } catch (err) {
      // The descent means "not placed". An unknown result may be live at the
      // venue, so it gets no tone: the caller's amber line says to go look.
      if (!(err instanceof OrderSubmissionUnknownError) && !(err instanceof DeskResolutionConfirmation)) {
        const snd = readSoundSettings();
        if (snd.orderRejectionSoundEnabled) playOrderRejectedTone(getChannelVolume('orderRejected'));
      }
      throw err;
    }
  },

  placeOrderSequence: async (input: OscarSequenceInput) => {
    let result: OscarSequenceResult;
    try {
      // Same boundary as postOrderRaw: retain exchangeIndex in the local Oscar
      // object/retry identity, but do not send it as routing authority. #575
      // owns the relay wire change.
      const { exchangeIndex: _observedExchangeIndex, ...wireInput } = input;
      const res = await fetchOrderWrite('Order sequence', '/api/kalshi/order-sequence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(wireInput),
        priority: 'high',
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        if (isLimitRejectionPayload(errData)) {
          throw new LimitRejectionError(errData.error, errData.code, errData.breach);
        }
        throw new Error(
          (errData as Record<string, string>).error ?? `Sequence failed (${res.status})`,
        );
      }
      result = (await res.json()) as OscarSequenceResult;
    } catch (err) {
      const snd = readSoundSettings();
      if (snd.orderRejectionSoundEnabled) playOrderRejectedTone(getChannelVolume('orderRejected'));
      throw err;
    }
    // 200 with per-leg statuses: tone on the overall outcome (a partial —
    // e.g. take landed, blocker failed — is a rejection-tone event; the
    // ticket renders the leg detail).
    const snd = readSoundSettings();
    if (result.ok) {
      if (snd.orderPlacedSoundEnabled) playOrderPlacedTone(getChannelVolume('orderPlaced'));
    } else if (snd.orderRejectionSoundEnabled) {
      playOrderRejectedTone(getChannelVolume('orderRejected'));
    }
    kickOrdersRefresh('order-sequence');
    return result;
  },

  sweepCloseLeg: async (ticker, side, quantity) => {
    let remaining = quantity;
    let residual: number | null = null;
    let placedTone = false;
    let sweepError: unknown = null;
    try {
      for (let attempt = 0; attempt < MAX_CLOSE_ATTEMPTS; attempt++) {
        // Marketable IOC at the 1¢ slippage floor — "sell into any bid the
        // book has". reduce_only so a stale count can't over-fill past flat
        // into the opposite side at the book extreme.
        const order = await postOrderRaw({
          ticker,
          side,
          action: 'sell',
          type: 'market',
          count: remaining,
          price: 1,
          reduceOnly: true,
        });
        if (!placedTone) {
          placedTone = true;
          const snd = readSoundSettings();
          if (snd.orderPlacedSoundEnabled) playOrderPlacedTone(getChannelVolume('orderPlaced'));
        }
        residual = residualOpenContracts(order, remaining);
        // Unverifiable (no fill counts on the response) — stop, don't blind-retry.
        if (residual === null) break;
        if (residual <= 0) {
          residual = 0;
          break;
        }
        // Partial fill: re-sweep ONLY the contracts still open.
        remaining = residual;
      }
    } catch (err) {
      // Don't decide success/failure yet. A reduce_only IOC fired at an already
      // flat leg — a routine close-time race (a concurrent fill closed the last
      // lot), or a reduce-to-zero reject Kalshi doesn't document — can throw on a
      // leg that is actually CLOSED. Settle it against the live position below
      // instead of blindly reporting a failure on a position that's already flat.
      sweepError = err;
    }

    // Read the TRUE open count from the live position — the same source the desk
    // UI trusts — but ONLY when refreshPositions FRESHLY succeeded this call
    // (connected, same connection generation, HTTP 200). A no-op/stale/empty
    // positions array must NEVER be read as proof of a flat leg: a disconnect mid
    // close could otherwise clamp a real residual to 0 and report a close as done
    // while contracts stay open. liveOpen === null means "couldn't confirm".
    // Reconcile when the IOC response leaves real-money ambiguity: a thrown sweep
    // (leg may already be flat), an opaque/null response, or a surviving residual.
    const needsLiveTruth = sweepError !== null || residual === null || residual > 0;
    let liveOpen: number | null = null;
    if (needsLiveTruth) {
      try {
        const fresh = await get().refreshPositions();
        if (fresh && get().status === 'connected') {
          const live = get().positions.find((p) => p.ticker === ticker && p.side === side);
          if (live) {
            liveOpen = Math.max(0, Math.round(live.position));
          } else {
            // Leg ABSENT from the positions read. In USER mode (and the null
            // pre-mode) that read is live Kalshi /portfolio/positions — paged to
            // exhaustion, authoritative — so absent means genuinely flat. In ORG
            // mode it's a reconstruction from the relay's fills ledger, which the
            // poller lags up to ~10s behind Kalshi; absent there can mean "fills
            // not ingested yet", NOT positively flat. Never read org-ledger absence
            // as proof of flat — leave liveOpen null ("couldn't confirm") so the
            // close warns instead of false-flatting hidden exposure on the gbuff
            // market-making lane.
            liveOpen = get().keyMode === 'org' ? null : 0;
          }
        }
      } catch {
        liveOpen = null;
      }
    }

    if (sweepError !== null) {
      // Suppress the error ONLY when the live position POSITIVELY confirms the leg
      // is already flat (the throw was a no-op on a closed leg). Otherwise the
      // failure is real or unconfirmable — surface it loudly, never as success.
      if (liveOpen === 0) {
        kickOrdersRefresh('sweep-close');
        return 0;
      }
      // Same rule as placeOrder: an unknown IOC result may have filled, so it
      // is not announced with the "not placed" descent.
      if (!(sweepError instanceof OrderSubmissionUnknownError)) {
        const snd = readSoundSettings();
        if (snd.orderRejectionSoundEnabled) playOrderRejectedTone(getChannelVolume('orderRejected'));
      }
      throw sweepError;
    }

    if (residual === null && liveOpen !== null) {
      // Opaque response, but a trustworthy live read resolved the leg: adopt it —
      // flat (0) → success, open (>0) → INCOMPLETE — instead of nagging UNVERIFIED
      // while the desk already shows the leg closed. An untrustworthy read leaves
      // liveOpen null → residual stays null → UNVERIFIED survives (no false flat).
      residual = liveOpen;
    } else if (residual !== null && residual > 0 && liveOpen !== null) {
      // Clamp a surviving residual to the trustworthy live count — kills a phantom
      // residual when reduce_only capped the last IOC below `remaining` (a stale
      // snapshot vs the true open count). If the read wasn't trustworthy (liveOpen
      // null), KEEP the response-derived residual so the close warns rather than
      // falsely reporting flat.
      residual = Math.min(residual, liveOpen);
    }
    kickOrdersRefresh('sweep-close');
    return residual;
  },

  cancelOrders: async (orderIds, options) => {
    // #241's state machine, batched (#272). The ONLY structural difference from
    // the single-order path is the claim: every ID in the batch is fenced in one
    // synchronous `set` before the first DELETE goes out. Claiming lazily, per
    // iteration, would leave every not-yet-reached ID clickable while the batch
    // ran — a single-row click would then send a duplicate DELETE for an order
    // the sweep is about to cancel, which is exactly the #241 defect at scale.
    const refreshAfter = options?.refreshAfter !== false;

    const unique: string[] = [];
    const seenIds = new Set<string>();
    for (const raw of orderIds) {
      const orderId = typeof raw === 'string' ? raw.trim() : '';
      if (!orderId || seenIds.has(orderId)) continue;
      seenIds.add(orderId);
      unique.push(orderId);
    }
    if (unique.length === 0) return [];

    const results = new Map<string, KalshiCancelBatchResult>();
    const claimed: Array<{ orderId: string; token: number }> = [];

    // ── Claim phase: no awaits between here and the `set`. ──
    let states = get().kalshiCancelStates;
    const claimedAtMs = Date.now();
    for (const orderId of unique) {
      if (isKalshiCancelPending(states[orderId])) {
        // Another surface (or an earlier batch) already owns this ID. Report it
        // rather than resending — a retry report must never resend a submitted
        // cancel.
        results.set(orderId, {
          orderId,
          status: 'already-pending',
          error: CANCEL_ALREADY_PENDING_MESSAGE,
        });
        continue;
      }
      const token = ++_cancelAttemptSeq;
      _cancelAttempts.set(orderId, token);
      clearCancelTimer(orderId); // a retry supersedes any timed-out attempt's timer
      states = beginKalshiCancel(states, orderId, claimedAtMs);
      claimed.push({ orderId, token });
    }
    if (claimed.length > 0) set({ kalshiCancelStates: states });

    try {
      // Sequential within the venue: the relay's Kalshi lane is a shared token
      // bucket, and a burst of parallel DELETEs buys nothing but rate-limit risk.
      for (const { orderId, token } of claimed) {
        try {
          await deleteKalshiOrder(orderId);
          forgetPlacementConfirmation(orderId);
        } catch (err) {
          // #968 review: a snapshot may have omitted the id while this DELETE
          // was in flight. The cancel failed, so that absence confirmed nothing;
          // never let it hide the order or call a relist "confirmed".
          forgetCancelTombstone(orderId);
          if (_cancelAttempts.get(orderId) === token) {
            forgetCancelAttempt(orderId);
            set({ kalshiCancelStates: clearKalshiCancel(get().kalshiCancelStates, orderId) });
          }
          results.set(orderId, {
            orderId,
            status: 'failed',
            error: err instanceof Error && err.message ? err.message : 'Cancel failed',
          });
          continue;
        }

        // 2xx means SUBMITTED, never removed (#241). The fence stays up until an
        // authoritative snapshot drops the ID.
        results.set(orderId, { orderId, status: 'submitted', error: null });

        // A newer attempt, a disconnect, or an authoritative snapshot may have
        // retired this attempt while the DELETE was in flight — never re-fence.
        if (_cancelAttempts.get(orderId) !== token) continue;
        const current = get().kalshiCancelStates;
        const awaiting = markKalshiCancelAwaitingRemoval(current, orderId, Date.now());
        if (awaiting === current) continue;
        set({ kalshiCancelStates: awaiting });
        armCancelSyncTimer(orderId, token);
      }
    } finally {
      // ONE refresh for the whole batch. `refreshAfter: false` hands that duty to
      // a mixed-venue caller that refreshes each venue exactly once itself.
      if (refreshAfter && claimed.length > 0) kickOrdersRefresh('cancel-batch');
    }

    return unique.map(
      (orderId) =>
        results.get(orderId) ?? { orderId, status: 'failed' as const, error: 'Cancel not attempted' },
    );
  },

  cancelOrdersByTickers: async (tickers, options) => {
    const tickerSet = new Set(tickers.map((t) => t.trim()).filter(Boolean));
    if (tickerSet.size === 0) return [];
    const surface = options?.surface;
    const refreshAfter = options?.refreshAfter !== false;

    // Locally-known targets — same claim/fence as cancelOrders so their rows
    // disable immediately and the #241 fence holds while the relay works.
    // The relay derives its OWN target set; ids it finds beyond ours (a
    // staler local list) come back in the results and simply need no local
    // fence release. A FROZEN set (the Spacebar pull) is the claim set as
    // given — the relay never widens past it, so neither do we.
    const localIds: string[] = [];
    if (options?.targets) {
      const seen = new Set<string>();
      for (const raw of options.targets) {
        const id = typeof raw === 'string' ? raw.trim() : '';
        if (!id || seen.has(id)) continue;
        seen.add(id);
        localIds.push(id);
      }
    } else {
      for (const o of get().openOrders) {
        if (!tickerSet.has(o.ticker)) continue;
        if (o.remainingCount <= 0) continue;
        localIds.push(o.orderId);
      }
    }

    const results = new Map<string, KalshiCancelBatchResult>();
    const claimed: Array<{ orderId: string; token: number }> = [];
    let states = get().kalshiCancelStates;
    const claimedAtMs = Date.now();
    for (const orderId of localIds) {
      if (isKalshiCancelPending(states[orderId])) {
        results.set(orderId, {
          orderId,
          status: 'already-pending',
          error: CANCEL_ALREADY_PENDING_MESSAGE,
        });
        continue;
      }
      const token = ++_cancelAttemptSeq;
      _cancelAttempts.set(orderId, token);
      clearCancelTimer(orderId);
      states = beginKalshiCancel(states, orderId, claimedAtMs);
      claimed.push({ orderId, token });
    }
    if (claimed.length > 0) set({ kalshiCancelStates: states });

    const releaseClaims = () => {
      let st = get().kalshiCancelStates;
      for (const { orderId, token } of claimed) {
        if (_cancelAttempts.get(orderId) !== token) continue;
        forgetCancelAttempt(orderId);
        st = clearKalshiCancel(st, orderId);
      }
      set({ kalshiCancelStates: st });
    };
    // The sequential per-id DELETE path (older relay / no answer). Claims are
    // released first (cancelOrders re-claims), and the pull's kill boundary
    // is re-established through the standalone authorizer when one is given.
    const fallbackToPerId = async () => {
      releaseClaims();
      if (options?.authorizeFallback) {
        try {
          await options.authorizeFallback();
        } catch (err) {
          throw err instanceof PullAllRefusedError
            ? err
            : new PullAllRefusedError(
                err instanceof Error && err.message ? err.message : 'relay did not authorize the pull',
              );
        }
      }
      return get().cancelOrders(localIds, { refreshAfter });
    };

    try {
      let res: Response;
      try {
        res = await fetchOrderWrite('Cancel sequence', '/api/kalshi/orders/cancel-sequence', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tickers: [...tickerSet],
            ...(surface ? { surface } : {}),
            ...(options?.attemptId ? { attemptId: options.attemptId } : {}),
            ...(options?.snapshotId ? { snapshotId: options.snapshotId } : {}),
            ...(options?.targets ? { targets: localIds } : {}),
          }),
          priority: 'high',
        });
      } catch {
        // Network failure before the relay answered: nothing was submitted —
        // release and fall back to the sequential path (which retries per-id).
        return await fallbackToPerId();
      }
      if (res.status === 404) {
        // Older relay without the route — byte-identical old behavior.
        return await fallbackToPerId();
      }
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const message =
          (errData as Record<string, string>).error ?? `Pull failed (${res.status})`;
        releaseClaims();
        if (surface === 'live_pull_all') {
          // The merged route answers non-2xx ONLY before execution begins
          // (gate, lookup, attempt row) — a refusal of the whole pull with
          // nothing sent, exactly what the standalone authorizer's failure
          // used to mean. Not a per-order failure: nothing to retry.
          throw new PullAllRefusedError(
            message,
            typeof (errData as Record<string, unknown>).code === 'string'
              ? ((errData as Record<string, string>).code)
              : null,
            res.status,
          );
        }
        for (const { orderId } of claimed) {
          results.set(orderId, { orderId, status: 'failed', error: message });
        }
        return localIds.map(
          (orderId) =>
            results.get(orderId) ?? { orderId, status: 'failed' as const, error: message },
        );
      }

      const payload = (await res.json().catch(() => null)) as {
        results?: Array<{ orderId?: unknown; status?: unknown; error?: unknown }>;
      } | null;
      const relayResults = Array.isArray(payload?.results) ? payload.results : [];
      const byId = new Map<string, { status: string; error: string | null }>();
      for (const r of relayResults) {
        if (typeof r?.orderId !== 'string' || typeof r?.status !== 'string') continue;
        byId.set(r.orderId, {
          status: r.status,
          error: typeof r.error === 'string' ? r.error : null,
        });
      }

      // A FROZEN id the relay's lookup did not list is not proven gone: that
      // lookup can be an org snapshot older than the order (a just-accepted
      // order, the hotkey second pass). Each one gets its own DELETE below and
      // counts as sent only on that DELETE's answer — never assumed.
      const unlisted: string[] = [];
      for (const { orderId, token } of claimed) {
        const relay = byId.get(orderId);
        if (relay == null && options?.targets) {
          if (_cancelAttempts.get(orderId) === token) {
            forgetCancelAttempt(orderId);
            set({ kalshiCancelStates: clearKalshiCancel(get().kalshiCancelStates, orderId) });
          }
          unlisted.push(orderId);
          continue;
        }
        if (relay == null || relay.status === 'canceled' || relay.status === 'already_gone') {
          // Venue-confirmed gone (or, on the ticker sweep, the relay's
          // fresher lookup no longer listed it). Fence moves to awaiting-
          // removal until an authoritative snapshot drops the id, exactly
          // like a submitted DELETE.
          forgetPlacementConfirmation(orderId);
          results.set(orderId, { orderId, status: 'submitted', error: null });
          if (_cancelAttempts.get(orderId) !== token) continue;
          const current = get().kalshiCancelStates;
          const awaiting = markKalshiCancelAwaitingRemoval(current, orderId, Date.now());
          if (awaiting === current) continue;
          set({ kalshiCancelStates: awaiting });
          armCancelSyncTimer(orderId, token);
        } else {
          if (_cancelAttempts.get(orderId) === token) {
            forgetCancelAttempt(orderId);
            set({ kalshiCancelStates: clearKalshiCancel(get().kalshiCancelStates, orderId) });
          }
          results.set(orderId, {
            orderId,
            status: 'failed',
            error: relay.error ?? 'Cancel failed',
          });
        }
      }
      if (unlisted.length > 0) {
        // Same attempt: the relay already recorded these ids in its frozen set.
        for (const r of await get().cancelOrders(unlisted, { refreshAfter: false })) results.set(r.orderId, r);
      }
    } finally {
      if (refreshAfter && (claimed.length > 0 || tickerSet.size > 0)) {
        kickOrdersRefresh('cancel-sequence');
      }
    }

    return localIds.map(
      (orderId) =>
        results.get(orderId) ?? { orderId, status: 'failed' as const, error: 'Cancel not attempted' },
    );
  },

  cancelOrder: async (orderId: string) => {
    // #241 — the user-facing single cancel. Delegates to the batch so there is
    // exactly ONE fenced cancel path in the app; the throwing contract callers
    // depend on is reconstructed from the batch's per-ID result.
    // replaceOrder deliberately bypasses all of this via requestKalshiCancel.
    const [result] = await get().cancelOrders([orderId]);
    if (!result) return;
    if (result.status === 'already-pending') throw new Error(CANCEL_ALREADY_PENDING_MESSAGE);
    if (result.status === 'failed') throw new Error(result.error ?? 'Cancel failed');
  },

  decreaseOrder: async (orderId: string, nextQuantity: number) => {
    if (!Number.isFinite(nextQuantity) || !Number.isInteger(nextQuantity) || nextQuantity < 1) {
      throw new Error('Decrease target must be a positive integer ≥ 1.');
    }
    const existing = get().openOrders.find((order) => order.orderId === orderId);
    if (existing && !canEditKalshiOrder(existing)) {
      throw new Error('This recovered order can be cancelled, but it cannot be edited safely.');
    }
    const res = await fetchOrderWrite('Decrease', `/api/kalshi/orders/${orderId}/decrease`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ nextQuantity }),
      priority: 'high',
    });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(
        (errData as Record<string, string>).error ?? `Decrease failed (${res.status})`,
      );
    }
    kickOrdersRefresh('decrease');
  },

  replaceOrder: (
    orderId: string,
    newPriceCents: number,
    newQuantity?: number,
    opts?: boolean | ReplaceOrderOptions,
  ) => announcingEditRefusals(async (markPlacing) => {
    const options: ReplaceOrderOptions =
      typeof opts === 'boolean' ? { forceCancelReplace: opts } : (opts ?? {});
    const forceCancelReplace = options.forceCancelReplace === true;
    // (0, 100) exclusive — sub-cent prices (58.3¢) are valid on deci_cent
    // markets. Tick alignment is NOT left to the venue on this path: the
    // validateReplacePriceCents gate below rejects a misaligned CHANGED price
    // client-side, before any venue leg, because a venue rejection would land
    // after the resting order was already cancelled.
    if (!Number.isFinite(newPriceCents) || newPriceCents <= 0 || newPriceCents >= 100) {
      throw new Error('Replacement price must be above 0 and below 100¢.');
    }
    const existing = get().openOrders.find((o) => o.orderId === orderId);
    if (!existing) {
      throw new Error('Order to replace was not found in the open-orders list.');
    }
    if (!canEditKalshiOrder(existing)) {
      throw new Error('This recovered order can be cancelled, but it cannot be edited safely.');
    }
    if (existing.type !== 'limit') {
      throw new Error('Only resting limit orders can be replaced.');
    }
    if (!Number.isFinite(existing.remainingCount) || existing.remainingCount <= 0) {
      throw new Error('Order remaining size is missing or invalid.');
    }
    // Resolve target quantity. Omitted → keep existing remaining. Validate
    // the same shape decreaseOrder enforces, here too, so an invalid value
    // can't slip into the cancel+place branch either.
    const targetQty = newQuantity ?? existing.remainingCount;
    if (!Number.isFinite(targetQty) || !Number.isInteger(targetQty) || targetQty < 1) {
      throw new Error('Replacement quantity must be a positive integer ≥ 1.');
    }
    // Side-native current price for the smart-route check. Kalshi often
    // returns only yes_price even for NO orders, so mirror the table/card
    // fallback and invert (exact mirror) when the matching side price is absent.
    const currentPriceCents =
      existing.side === 'yes'
        ? (existing.yesPrice ?? (existing.noPrice != null ? mirrorCents(existing.noPrice) : null))
        : (existing.noPrice ?? (existing.yesPrice != null ? mirrorCents(existing.yesPrice) : null));
    // Local no-op: nothing to send. Kept client-side so an unchanged edit
    // never costs a relay round trip (the route would answer `noop` anyway).
    if (currentPriceCents != null && newPriceCents === currentPriceCents && targetQty === existing.remainingCount) {
      return { orderId: existing.orderId };
    }
    // Smart route: a pure quantity-shrink at the same price stays queue-
    // preserving via the /decrease endpoint. Anything else (price moved,
    // qty increased, qty kept but price moved) routes through the replace
    // route (amend or cancel+place, relay's choice) and may lose queue
    // position. Polymarket has no /decrease so its replace is always
    // cancel+place (handled in usePolymarketRelayOrders).
    //
    // The caller can opt OUT of the smart route via forceCancelReplace,
    // when the trader explicitly wants to lose queue position (e.g. to
    // refresh / cycle to the back). Modal exposes that as a checkbox.
    if (
      !forceCancelReplace &&
      currentPriceCents != null &&
      newPriceCents === currentPriceCents &&
      targetQty < existing.remainingCount
    ) {
      await get().decreaseOrder(orderId, targetQty);
      return { orderId: existing.orderId, route: 'decrease' };
    }
    // Last-line tick revalidation BEFORE any venue leg (DNL round-3 finding 1):
    // a venue tick rejection on the replacement lands after the original is
    // gone. Hydra's Kalshi priceRanges refresh through discovery and can
    // briefly trail a live tick-regime change, so a CHANGED price remains
    // whole-cent-only here (valid across published structures). The
    // unchanged current price always passes by existence. New-order surfaces
    // use the range contract because a venue rejection there loses no order.
    const tickCheck = validateReplacePriceCents({
      newPriceCents,
      currentPriceCents,
      tickCents: null,
      unknownTickPolicy: 'whole-cent',
    });
    if (!tickCheck.ok) {
      throw new Error(tickCheck.error);
    }
    // Never reprice off an unconfirmed quote (main #847): the guard covers the
    // replace route AND the forced cancel→place pair below.
    // A post-only replacement priced through the book is refused by the
    // venue AFTER the cancel on the cancel→place leg, which destroys the
    // order (main 616006a4). Read the live book first; an unknown book is
    // not a refusal (the venue still guards). The amend leg is atomic, so
    // this only ever saves the trader an order.
    if (postOnlyWouldCross(existing.ticker, existing.side, existing.action, newPriceCents) === true) {
      throw new Error(EDIT_WOULD_CROSS_MESSAGE);
    }
    assertSourceConfirmed({ ticker: existing.ticker });
    // "Cycle" (forceCancelReplace) is a deliberate back-of-queue request. The
    // replace route smart-routes server-side (same price + smaller → decrease,
    // which KEEPS the queue slot), so the only honest way to honour the force
    // is today's explicit cancel→place pair.
    if (forceCancelReplace) {
      return legacyCancelThenPlace(get, existing, newPriceCents, targetQty, markPlacing);
    }
    // ── Speed-demon D1: ONE relay round trip ────────────────────────────────
    // POST /api/kalshi/orders/:id/replace — the relay cancels+places (or
    // amends where the venue allows it) under its own limit gate with zero
    // DB statements between the venue legs. A 404 is an older relay without
    // the route: fall back to the two-call path verbatim.
    const clientOrderId = options.clientOrderId ?? `rp-${crypto.randomUUID()}`;
    const body: Record<string, unknown> = {
      ticker: existing.ticker,
      side: existing.side,
      action: existing.action,
      count: targetQty,
      // 4 dp carries the 0.01¢ grain exactly (sub-cent deci_cent markets).
      [`${existing.side}_price_dollars`]: (newPriceCents / 100).toFixed(4),
      // post_only=true is the safe default for an edit so the replacement
      // can't accidentally become a market sweep.
      post_only: true,
      // The original vanishing between the trader's look and the press means
      // it filled or was pulled — placing anyway would open a position the
      // trader did not choose. The relay reports `already_gone` instead.
      placeIfGone: false,
      client_order_id: clientOrderId,
    };
    if (currentPriceCents != null) body.prevPriceCents = currentPriceCents;
    // GTS stays GTS: the relay carries the original's expiration onto every leg.
    if (existing.expirationTs != null) body.expiration_ts = existing.expirationTs;
    // Good to Start audit stamp (main 838a90936): the relay keeps its start
    // watcher on the replacement only when the original's own placement was
    // GTS (same user, ticker and expiry); a Custom expiry carries nothing. The
    // route rebuilds the venue body from parsed fields, so this never reaches
    // Kalshi.
    if (existing.expirationTs != null) body.__gts = { v: 1, carryFrom: orderId };
    // A close stays a close (main 2c82c5e2): a reduce-only original re-states
    // the flag on the replacement; unknown is not forced — a reduce-only order
    // whose position is already flat would be refused after the cancel.
    if (existing.reduceOnly === true) body.reduce_only = true;
    if (options.auditMeta) body.__lt2 = options.auditMeta;
    if (options.attackMeta) body.__attack = options.attackMeta;

    // From here the relay may cancel and place: refusals after this point are
    // the route's own (definite or unknown), and ring their own tones below.
    markPlacing();
    let res: Response;
    probeMark('request');
    try {
      res = await fetchOrderWrite('Replace', `/api/kalshi/orders/${orderId}/replace`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        priority: 'high',
      });
    } catch (err) {
      // The response was lost after submission began: the cancel leg may
      // have run and the replacement may be live. Never a rejection.
      if (err instanceof OrderHttpTimeoutError) throw new OrderSubmissionUnknownError(err.message);
      throw new OrderSubmissionUnknownError();
    }
    if (res.status === 404) {
      markPlacing(false); // an older relay: nothing ran, the fallback's own refusals ring
      return legacyCancelThenPlace(get, existing, newPriceCents, targetQty, markPlacing);
    }
    if (typeof res.headers?.get === 'function') {
      const stages = parseServerTiming(res.headers.get('server-timing'));
      if (stages) probeAttachServerTiming(stages);
    }
    const snd = readSoundSettings();
    const rejectTone = () => {
      if (snd.orderRejectionSoundEnabled) playOrderRejectedTone(getChannelVolume('orderRejected'));
    };
    if (!res.ok) {
      const errData = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      // The descent means "not placed" (main 616006a4 / ae0b30ab): an ambiguous
      // cancel or an unknown placement may be live at the venue, so those get
      // no tone — only a definite refusal does.
      if (errData.cancelOutcome === 'ambiguous' || errData.code === 'REPLACE_AMBIGUOUS') {
        throw new ReplaceAmbiguousError(
          typeof errData.error === 'string' ? errData.error : undefined,
        );
      }
      if (isLimitRejectionPayload(errData)) {
        rejectTone();
        throw new LimitRejectionError(errData.error, errData.code, errData.breach);
      }
      if (errData.code === 'KALSHI_ORDER_STATE_UNKNOWN') {
        throw new OrderSubmissionUnknownError(
          typeof errData.error === 'string' ? errData.error : undefined,
        );
      }
      // A pre-flight refusal carries the relay's {error, code} envelope even
      // on 502; a bare 408/5xx with no code is a front proxy losing the relay
      // mid-request, and the venue legs may have run.
      if (typeof errData.code !== 'string' && (res.status === 408 || res.status >= 500)) {
        throw new OrderSubmissionUnknownError();
      }
      rejectTone();
      throw new Error(
        typeof errData.error === 'string' ? errData.error : `Replace failed (${res.status})`,
      );
    }
    let data: Record<string, unknown>;
    try {
      data = (await res.json()) as Record<string, unknown>;
    } catch {
      throw new OrderSubmissionUnknownError();
    }
    const route = pickStr(data.route) as ReplaceRoute | undefined;
    const cancelOutcome = pickStr(data.cancelOutcome);
    // The relay's `placed` is the RAW venue body of the leg that left an order
    // resting (ReplaceResponseBody.placed), null when nothing was placed or
    // changed. `true` is still read as placed; null / false / absent are not.
    const placed = data.placed != null && data.placed !== false;
    const canceled = data.canceled === true;
    const routeError =
      typeof data.error === 'string' && data.error !== '' ? data.error : null;
    if (cancelOutcome === 'ambiguous') {
      // Unknown class: the original may or may not rest. No tone.
      throw new ReplaceAmbiguousError(routeError ?? undefined);
    }
    if (route === 'noop') {
      return { orderId: existing.orderId, route };
    }
    if (cancelOutcome === 'already_gone' && !placed) {
      // The original left the book before the relay reached it (fill or
      // pull). Nothing was placed (placeIfGone:false) — drop our projection
      // of it and say so.
      forgetPlacementConfirmation(orderId);
      kickOrdersRefresh('replace');
      rejectTone();
      throw new Error(
        routeError ?? 'Original order was already gone (filled or cancelled) — nothing placed.',
      );
    }
    if (cancelOutcome === 'failed' && !placed) {
      rejectTone();
      throw new Error(`Could not cancel original order: ${routeError ?? 'Cancel failed'}`);
    }
    if (!placed) {
      // Cancelled (or amend refused after the cancel) and nothing rests.
      if (canceled) {
        forgetPlacementConfirmation(orderId);
        // #971: the relay's cancel leg retired the id; hide it once a
        // snapshot omits it, so an older snapshot can't re-show it.
        noteEditRetiring(orderId);
        kickOrdersRefresh('replace');
        // The cancel landed and the replacement's result is unknown: it may
        // be live. Same unknown class (and words) as the browser fallback's
        // cancel→place pair; never a rejection, no tone.
        if (data.code === 'KALSHI_ORDER_STATE_UNKNOWN') {
          throw new OrderSubmissionUnknownError(EDIT_REPLACEMENT_UNKNOWN_MESSAGE);
        }
      }
      rejectTone();
      throw new Error(
        canceled
          ? `Original order was cancelled, but the replacement failed to place: ${routeError ?? 'Place failed'}`
          : (routeError ?? 'Replace failed — the original order still rests.'),
      );
    }
    const newId = pickStr(data.orderId) ?? existing.orderId;
    // ── Projection ──────────────────────────────────────────────────────────
    // The per-ticker lane is keyed by id, but the CHIP must move: an amend
    // keeps the id and changes the price, so the old row is retired whenever
    // either the id or the price changed, then the surviving id is projected
    // at the new price. Same resting-evidence gate as placeOrder: the route
    // said it placed a post-only limit, so it rests until a snapshot says
    // otherwise.
    const idChanged = newId !== existing.orderId;
    const priceChanged = currentPriceCents == null || newPriceCents !== currentPriceCents;
    if (idChanged || priceChanged) forgetPlacementConfirmation(existing.orderId);
    // #971: a cancel+place route retired the old id (an amend keeps it).
    if (idChanged) noteEditRetiring(existing.orderId);
    const replacement: KalshiOrder = {
      ...existing,
      orderId: newId,
      status: 'resting',
      count: targetQty,
      remainingCount: targetQty,
      yesPrice: existing.side === 'yes' ? newPriceCents : undefined,
      noPrice: existing.side === 'no' ? newPriceCents : undefined,
      clientOrderId,
      ...(idChanged ? { createdTime: new Date().toISOString() } : {}),
    };
    _placementConfirmations.set(newId, {
      order: replacement,
      expiresAtMs: Date.now() + KALSHI_PLACEMENT_CONFIRMATION_TIMEOUT_MS,
    });
    set({
      openOrders: [
        ...get().openOrders.filter(
          (candidate) => candidate.orderId !== existing.orderId && candidate.orderId !== newId,
        ),
        replacement,
      ],
    });
    emitPlacement({
      ticker: existing.ticker,
      orderId: newId,
      side: existing.side,
      action: existing.action,
      priceCents: newPriceCents,
      remainingCount: targetQty,
    });
    if (snd.orderPlacedSoundEnabled) playOrderPlacedTone(getChannelVolume('orderPlaced'));
    kickOrdersRefresh('replace');
    return { orderId: newId, route };
  }),
}));

/**
 * Today's two-call edit — the pre-D1 path, verbatim. Reached when the relay
 * has no replace route (404) or the caller forced a cancel+place ("Cycle").
 *
 * 1) Cancel the original. If this rejects, the original is still resting —
 *    surface the error and bail before touching place.
 * 2) Place new at the new price + qty, preserving every other param.
 *    Trade-guard / limit enforcement runs at the relay's checkOrderLimit
 *    chokepoint; the browser-side useTradeGuard wraps the dashboard
 *    trade-ticket path but is not on this edit path.
 */
async function legacyCancelThenPlace(
  get: () => AccountState,
  existing: KalshiOrder,
  newPriceCents: number,
  targetQty: number,
  markPlacing?: () => void,
): Promise<ReplaceOrderResult> {
  // The identity the cancel acts for (main 616006a4). A sign-in from another
  // tab while the cancel is in flight would otherwise place the replacement
  // as the new account.
  const sessionEpoch = getAuthSessionEpoch();
  try {
    // The UNFENCED leg (#241): an edit must not arm a cancel fence on the
    // order it is replacing — that would disable the row's own Edit control
    // and could raise the cancel-timeout notice on a successful edit.
    await requestKalshiCancel(existing.orderId);
    // #971: the unfenced leg never reaches the fence's confirmation, so
    // track the id until a snapshot omits it, whether or not the
    // replacement then places; after that an older snapshot can't re-show it.
    noteEditRetiring(existing.orderId);
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Cancel failed';
    throw new Error(`Could not cancel original order: ${message}`);
  }
  if (getAuthSessionEpoch() !== sessionEpoch) {
    throw new Error(
      'Original order was cancelled, but the account changed. No replacement was placed; check the original account.',
    );
  }
  const expirationTime =
    existing.expirationTs != null
      ? new Date(existing.expirationTs * 1000).toISOString()
      : undefined;
  let placed: KalshiOrder;
  markPlacing?.();
  try {
    placed = await get().placeOrder({
      ticker: existing.ticker,
      side: existing.side,
      action: existing.action,
      type: 'limit',
      count: targetQty,
      price: newPriceCents,
      postOnly: true,
      timeInForce: 'good_till_canceled',
      ...(expirationTime !== undefined ? { expirationTime } : {}),
      // The relay keeps its Good to Start watcher on the replacement only
      // when the original's own placement was GTS (same user, ticker and
      // expiry); a Custom expiry carries nothing.
      ...(expirationTime !== undefined ? { goodToStart: { carryFrom: existing.orderId } } : {}),
      // A close stays a close (main 2c82c5e2); unknown is not forced.
      ...(existing.reduceOnly === true ? { reduceOnly: true } : {}),
    });
  } catch (err) {
    // The venue may hold the replacement: say so, and keep it an unknown
    // result so no caller presents it as a failure that invites a retry.
    if (err instanceof OrderSubmissionUnknownError) {
      throw new OrderSubmissionUnknownError(EDIT_REPLACEMENT_UNKNOWN_MESSAGE);
    }
    const message = err instanceof Error ? err.message : 'Place failed';
    throw new Error(
      `Original order was cancelled, but the replacement failed to place: ${message}`,
    );
  }
  // placeOrder falls back to '' when the venue response carries no id —
  // the replacement may be resting, but nothing downstream can key off it.
  if (!placed.orderId) {
    throw new Error(
      'Replacement was submitted but the venue returned no order id — refresh open orders to reconcile.',
    );
  }
  return { orderId: placed.orderId, route: 'legacy_cancel_place' };
}

// ── relay push integration ──────────────────────────────────────────────────
// Frames carry the SAME payloads as the REST endpoints the poll tick hits, so
// the existing parsers consume them unchanged. Every handler gates on
// status==='connected' — a frame racing a disconnect can't resurrect state
// (same job the poll path's generation guard does). Fills route through
// ingestFillBatch — the same shared path the REST bootstrap uses (#769) — so a
// batched frame cannot retain a different set than a batched poll.

// #1205: under push the 5 s poll — the only re-read of the REST balance — is
// off, so the balance topic carries the trader's current org-balance
// visibility (never a figure). Hidden applies at once and outranks any read
// already in flight. A visible frame with no figure on screen takes a
// sequenced read: hidden until now, an unhide whose read failed, or an
// org-mode connect whose venue reads failed (which used to stay "—" until a
// reconnect under push). The topic's re-send (on the relay's 30 s tick, so
// within about 40 s, and as often as every 10 s while fills are being
// published) retries until a read succeeds. Only org-key traders get these
// frames. Other connections get the topic as unavailable: personal-key users
// keep the 5 s poll, while org-key managers and admins (whose core topics are
// pushed, so their poll is off) keep the balance read at connect.
subscribePush('balance', (payload) => {
  const st = useAccountStore.getState();
  if (st.status !== 'connected') return;
  const hidden = (payload as { hidden?: unknown } | null)?.hidden;
  if (hidden === true) {
    // Always outrank reads in flight — even when already showing Hidden, an
    // unhide frame may have started a read that must not land after this.
    _balanceAppliedSeq = ++_balanceReadSeq;
    if (!st.balanceHidden || st.balance !== null) useAccountStore.setState({ balance: null, balanceHidden: true });
  } else if (hidden === false && (st.balanceHidden || st.balance === null)) {
    readPolledBalance(_connectionGeneration, true);
  }
});

subscribePush('positions', (payload) => {
  const st = useAccountStore.getState();
  if (st.status !== 'connected') return;
  // Same readable-or-not contract as the REST paths (#881 review F4): a
  // malformed frame keeps the last-good list and records the failure; it
  // never blanks the account.
  const read = readPositionsSnapshot(payload);
  if (!read.ok) {
    console.warn(
      `[accountStore] ${new Date().toISOString()} source=push positions refresh failed (${st.positionsSync.consecutiveFailures + 1}x): ${read.error}`,
    );
    useAccountStore.setState({ positionsSync: positionsSyncAfterFailure(st.positionsSync, read.error) });
    return;
  }
  useAccountStore.setState({ positions: read.positions, positionsSync: positionsSyncAfterSuccess(st.positionsSync) });
});

subscribePush('orders', (payload) => {
  const st = useAccountStore.getState();
  if (st.status !== 'connected') return;
  const orders = parseOrdersSnapshot(payload);
  if (orders === null) {
    const failures = st.ordersSync.consecutiveFailures + 1;
    console.warn(
      `[accountStore] ${new Date().toISOString()} source=push open-orders refresh failed (${failures}x): malformed orders payload`,
    );
    useAccountStore.setState({
      ordersSync: {
        ...st.ordersSync,
        lastErrorMs: Date.now(),
        lastError: 'malformed orders payload',
        consecutiveFailures: failures,
      },
    });
    return;
  }
  // #968: a frame older than an HTTP read already applied must not roll the
  // list back, nor restamp the sync clock the newer read already set.
  const asOf = readOrdersAsOf(payload);
  if (_ordersVintage.superseded(currentOrdersScopeKey(), asOf)) return;
  const admitted = admitOrdersSnapshot(st.kalshiCancelStates, orders, asOf);
  const reconciledOrders = reconcilePlacementConfirmations(admitted.orders);
  if (reconciledOrders.length === 0) logOpenOrdersEmptyTransition('push', st.openOrders.length);
  // Deploy C: a validated frame is what stands the post-write REST fallback down.
  _lastOrdersFrameAt = Date.now();
  useAccountStore.setState({
    openOrders: reconciledOrders,
    // Push orders are org-scoped. They cannot establish personal-key evidence,
    // or restore authority across a reconnect before a request-scoped read.
    ordersScopeKey: st.keyMode === 'org' && st.ordersScopeKey === currentOrdersScopeKey()
      ? st.ordersScopeKey : null,
    kalshiCancelStates: admitted.kalshiCancelStates,
    // Stream frames are the orders-sync heartbeat now — the staleness badge
    // (Jun 11 incident guard) reflects push freshness in push mode.
    ordersSync: {
      lastSuccessMs: Date.now(),
      lastErrorMs: st.ordersSync.lastErrorMs,
      lastError: st.ordersSync.lastError,
      consecutiveFailures: 0,
    },
  });
});

subscribePush('pnl', (payload) => {
  const st = useAccountStore.getState();
  if (st.status !== 'connected') return;
  const p = payload as {
    realizedPnlCents?: number;
    realizedFeesCents?: number | null;
    realizedPnlNetCents?: number | null;
    openNotionalCents?: number;
  };
  useAccountStore.setState({
    realizedPnlCents: p.realizedPnlCents ?? st.realizedPnlCents,
    realizedFeesCents:
      p.realizedFeesCents === undefined ? st.realizedFeesCents : p.realizedFeesCents,
    realizedPnlNetCents:
      p.realizedPnlNetCents === undefined ? st.realizedPnlNetCents : p.realizedPnlNetCents,
    openNotionalCents: p.openNotionalCents ?? st.openNotionalCents,
  });
});

subscribePush('fills', (payload, clock) => {
  const st = useAccountStore.getState();
  if (st.status !== 'connected') return;
  // Fail-closed (#499): a malformed push frame is dropped whole rather than
  // defaulted into a synthetic row. Push fills are incremental, not a history
  // read, so they never latch fillsSynced.
  const fills = parseFillsSnapshot(payload, st.keyMode === 'org' ? 'ledger' : 'venue');
  if (fills === null) {
    // #499 unchanged: the frame lands nothing. #1025: it owes a read. A new
    // frame is new information (the bad row may have moved), so read now.
    retireStaleFillsWork();
    recordFillsReadFailure('push', fillsRejectionReason(payload));
    _fillsWorkScope = historicalReadScope();
    _fillsFailureSeq += 1;
    _fillsReadOwed = true;
    _fillsReadDueAtMono = performance.now();
    if (_stopFillPollTimer) return;
    reconcilePolling(true);
    return;
  }
  st.ingestFillBatch(fills, clock);
});
